/**
 * Message viewer component -- displays full message in sandboxed iframe.
 *
 * Passive renderer. The list owns "what's focused"; this module exposes
 * `showMessage(accountId, uid, folderId, envelope?)` and `clearViewer()`
 * as the only ways content reaches the pane. There is no subscription —
 * if the list never tells the viewer about a focus change, the viewer
 * simply doesn't update. That's the property that makes summary↔preview
 * drift impossible: there is exactly one path into this pane.
 */

import { getMessage, updateFlags, allowRemoteContent, flagSenderOrDomain, getAttachment, openAttachment, getMessageSource, addContact, listContacts, upsertContact, unsubscribeOneClick, addPreferredContact, onEvent, subscribeStore } from "../lib/api-client.js";
import { showContextMenu } from "./context-menu.js";
import type { MenuItem } from "./context-menu.js";
import type { ListMessage } from "../lib/message-state.js";
import { updateMessageFlags as stateUpdateFlags } from "../lib/message-state.js";
import { setRowSeen } from "./message-list.js";
import { setSeen, seenOf } from "@bobfrankston/mailx-types";

/** Currently displayed message (for reply/forward) */
let currentMessage: any = null;
let currentAccountId: string = "";
// Blob URLs created for attachment drag-out (revoked when the next message's
// chips render, so they don't accumulate across the session).
const dragOutUrls: string[] = [];
let showMessageGeneration = 0; // Cancel stale fetches
let retryCount = 0;
/** Last envelope handed in by the list — used for envelope-first paint
 *  on retry, where we don't have a fresh row click to pass it again. */
let lastEnvelope: ListMessage | null = null;

/** Parsed-body LRU. Click → render must be synchronous; the only way to
 *  honor that is to keep the parsed result in WebView memory keyed by
 *  (accountId, uid). Bounded so a long session doesn't eat hundreds of
 *  MB of decoded HTML. Hit = render now, no IPC, no spinner, no
 *  "Loading…". Miss = paint the envelope's preview as a transient body
 *  and swap in the real one when the IPC returns (also caches). */
const PARSED_CACHE_LIMIT = 64;
const parsedCache = new Map<string, any>(); // key: `${accountId}:${folderId}:${uid}` → full message obj
// folderId is required in the key: IMAP UIDs are per-folder, so the SAME
// numeric UID (e.g. 196) refers to entirely different messages in INBOX vs
// Outbox vs Sent. Keying on (accountId, uid) alone caused the Outbox header
// to render with the body of an INBOX message at the same UID.
function parsedCacheGet(accountId: string, folderId: number | undefined, uid: number): any | null {
    if (folderId == null) return null; // no folderId → can't safely identify; skip cache
    const key = `${accountId}:${folderId}:${uid}`;
    const v = parsedCache.get(key);
    if (!v) return null;
    parsedCache.delete(key);
    parsedCache.set(key, v);
    return v;
}
function parsedCachePut(accountId: string, folderId: number | undefined, uid: number, msg: any): void {
    if (folderId == null) return; // no folderId → don't poison the cache
    const key = `${accountId}:${folderId}:${uid}`;
    parsedCache.delete(key);
    parsedCache.set(key, msg);
    while (parsedCache.size > PARSED_CACHE_LIMIT) {
        const oldest = parsedCache.keys().next().value;
        if (oldest === undefined) break;
        parsedCache.delete(oldest);
    }
}
/** Drop one entry — called when flags change so re-render reads fresh state. */
export function invalidateParsedCache(accountId: string, folderId: number | undefined, uid: number): void {
    if (folderId == null) return;
    parsedCache.delete(`${accountId}:${folderId}:${uid}`);
}

/** Session-scoped "user clicked Allow on this message" set. Once a uuid
 *  is in here, any subsequent showMessage call treats `hasRemoteContent`
 *  as false regardless of what the daemon returned — covers the race
 *  where the allowlist persist hasn't reached disk yet, the daemon
 *  re-parses and reports remote content again, and a folder refresh
 *  re-renders the banner on top of the unblocked iframe (Bob 2026-05-09:
 *  "still showing the blocked header" while content was loaded). */
const sessionAllowedRemote = new Set<string>();

/** Per-uid recent body-fetch errors. Populated by the bodyFetchError
 *  listener so the "No content" code path can show the real reason
 *  instead of a generic placeholder. Keyed `${accountId}:${uid}`. */
const recentFetchErrors = new Map<string, { error: string; transient: boolean; when: number }>();
function markSessionAllowed(accountId: string, uid: number): void {
    sessionAllowedRemote.add(`${accountId}:${uid}`);
}
function isSessionAllowed(accountId: string, uid: number): boolean {
    return sessionAllowedRemote.has(`${accountId}:${uid}`);
}

export function getCurrentMessage(): { accountId: string; message: any } | null {
    if (!currentMessage) return null;
    return { accountId: currentAccountId, message: currentMessage };
}

/** Build the body-context menu items (right-click outside a link in the
 *  preview iframe). Called from app.ts's window message listener after the
 *  iframe inline script postMessages a "previewContextMenu" event with the
 *  current selection. Translate routes to the configured AI provider via
 *  aiTransform; "Translate via Google" opens translate.google.com in the
 *  default browser as a no-account-needed alternative. */
export function showPreviewBodyMenu(absX: number, absY: number, selectedText: string, sourceWindow: Window | null, linkUrl?: string, linkText?: string, imgSrc?: string): void {
    const pct = Math.round(previewZoom * 100);
    const runSearch = (query: string): void => {
        const input = document.getElementById("search-input") as HTMLInputElement | null;
        if (!input) return;
        input.value = query;
        input.dispatchEvent(new Event("input"));
        input.dispatchEvent(new KeyboardEvent("keydown", { key: "Enter", bubbles: true }));
        input.focus();
        // Narrow mode shows ONE pane. A search launched from the viewer used
        // to leave the (now stale/empty) viewer on screen — no search bar,
        // no results, "a blank screen and no clue what is happening" (Bob
        // 2026-07-13, Android). Flip to the list pane so the populated
        // search bar and the result list are what the user actually sees.
        document.getElementById("message-viewer")?.classList.remove("narrow-active");
        document.getElementById("message-list")?.classList.remove("narrow-hidden");
    };
    // Send a command back into the iframe (Copy, Select all) — the iframe
    // owns its own document and these only work in the iframe's context.
    const tellIframe = (cmd: string): void => {
        try { sourceWindow?.postMessage({ type: "previewCommand", cmd }, "*"); } catch { /* */ }
    };
    const fullBody = (): string => {
        try {
            for (const f of Array.from(document.querySelectorAll("iframe"))) {
                if ((f as HTMLIFrameElement).contentWindow === sourceWindow) {
                    return (f as HTMLIFrameElement).contentDocument?.body?.innerText || "";
                }
            }
        } catch { /* */ }
        return "";
    };
    const items: MenuItem[] = [];
    // Link actions first (when right-click was over a link). Quora-style
    // pages are wall-to-wall links — without this, body actions like
    // Translate were unreachable because every long-press hit a link.
    if (linkUrl) {
        const guessName = (() => {
            try { const u = new URL(linkUrl); const last = u.pathname.split("/").pop() || ""; return last && last.includes(".") ? last : ""; }
            catch { return ""; }
        })();
        items.push(
            { label: "Open link in browser", action: () => { const api = (window as any).mailxapi; if (api?.openExternal) api.openExternal(linkUrl); else window.open(linkUrl, "_blank", "noopener,noreferrer"); } },
            { label: guessName ? `Save link as "${guessName}"…` : "Save link as…", action: () => {
                const a = document.createElement("a"); a.href = linkUrl;
                a.download = guessName || ""; a.style.display = "none";
                document.body.appendChild(a); a.click(); setTimeout(() => a.remove(), 1000);
            } },
            { label: "Copy link URL", action: async () => {
                try { await navigator.clipboard.writeText(linkUrl); } catch { prompt("URL:", linkUrl); }
            } },
            { label: "Copy link text", action: async () => {
                try { await navigator.clipboard.writeText(linkText || linkUrl); } catch { prompt("Text:", linkText || linkUrl); }
            } },
            { label: "", action: () => {}, separator: true },
        );
    }
    // Image actions when the right-click was over an <img>. Lets the user pull
    // an image out of a received message and into a reply (Copy image → Ctrl+V
    // in compose, which embeds it) or save it to disk. Bob 2026-06-01.
    if (imgSrc) {
        const guessImgName = (() => {
            try { const u = new URL(imgSrc); const last = u.pathname.split("/").pop() || ""; return last && last.includes(".") ? last : "image.png"; }
            catch { return "image.png"; }
        })();
        items.push(
            { label: "Copy image", action: async () => {
                try {
                    const resp = await fetch(imgSrc);
                    const blob = await resp.blob();
                    // Transcode to PNG via canvas — WebView2's clipboard image
                    // write is reliable for PNG; arbitrary source types (webp,
                    // gif, jpeg) may not round-trip directly.
                    const bmp = await createImageBitmap(blob);
                    const canvas = document.createElement("canvas");
                    canvas.width = bmp.width; canvas.height = bmp.height;
                    canvas.getContext("2d")!.drawImage(bmp, 0, 0);
                    const png: Blob = await new Promise((res, rej) =>
                        canvas.toBlob(b => b ? res(b) : rej(new Error("toBlob failed")), "image/png"));
                    await navigator.clipboard.write([new ClipboardItem({ "image/png": png })]);
                } catch (e: any) {
                    // cid: inline images can't be fetched from the parent
                    // context — surface rather than fail silently.
                    alert(`Couldn't copy image: ${e?.message || e}${imgSrc.startsWith("cid:") ? " (inline cid: image — try Save instead, or right-click → save in the message)" : ""}`);
                }
            } },
            { label: `Save image as "${guessImgName}"…`, action: () => {
                const a = document.createElement("a"); a.href = imgSrc;
                a.download = guessImgName; a.style.display = "none";
                document.body.appendChild(a); a.click(); setTimeout(() => a.remove(), 1000);
            } },
            { label: "", action: () => {}, separator: true },
        );
    }
    items.push(
        { label: "Copy", action: () => tellIframe("copy") },
        { label: "Select all", action: () => tellIframe("selectAll") },
    );
    if (selectedText) {
        items.push(
            { label: "", action: () => {}, separator: true },
            { label: `Search messages for "${selectedText.length > 40 ? selectedText.slice(0, 40) + "…" : selectedText}"`,
              action: () => runSearch(selectedText) },
            { label: "Copy as quoted (> prefix)",
              action: async () => {
                  const quoted = selectedText.split(/\r?\n/).map(l => "> " + l).join("\n");
                  try { await navigator.clipboard.writeText(quoted); } catch { /* */ }
              } },
        );
    }
    const senderAddr = currentMessage?.from?.address || "";
    if (senderAddr) {
        items.push({ label: `Search messages from ${senderAddr}`, action: () => runSearch(`from:${senderAddr}`) });
    }
    items.push(
        { label: "", action: () => {}, separator: true },
        { label: selectedText ? "Translate selection (AI)" : "Translate message (AI)",
          action: () => translateAndShow(selectedText || fullBody()) },
        { label: selectedText ? "Translate selection (Google Translate)" : "Translate message (Google Translate)",
          action: () => {
              const text = (selectedText || fullBody()).slice(0, 4500); // GT URL has practical limits
              const url = `https://translate.google.com/?sl=auto&tl=en&op=translate&text=${encodeURIComponent(text)}`;
              const api = (window as any).mailxapi;
              if (api?.openExternal) api.openExternal(url); else window.open(url, "_blank", "noopener,noreferrer");
          } },
        { label: "", action: () => {}, separator: true },
        // Zoom group — current level (resets on click) first, then in / out.
        { label: `Zoom: ${pct}% — reset`, action: () => { for (const f of Array.from(document.querySelectorAll("iframe"))) { const d = (f as HTMLIFrameElement).contentDocument; if (d) { setZoom(1, d); break; } } } },
        { label: "Zoom in  (+)", action: () => { for (const f of Array.from(document.querySelectorAll("iframe"))) { const d = (f as HTMLIFrameElement).contentDocument; if (d) { setZoom(previewZoom + ZOOM_STEP, d); break; } } } },
        { label: "Zoom out (−)", action: () => { for (const f of Array.from(document.querySelectorAll("iframe"))) { const d = (f as HTMLIFrameElement).contentDocument; if (d) { setZoom(previewZoom - ZOOM_STEP, d); break; } } } },
        { label: "", action: () => {}, separator: true },
        { label: "Print… (Ctrl+P)", action: () => printCurrentMessage() },
        { label: "Save message (.eml)…", action: () => { void saveCurrentMessageAsEml(); } },
        { label: "Save message as HTML…", action: () => { void saveCurrentMessageAsHtml(); } },
    );
    showContextMenu(absX, absY, items);
}

/** Toggle preview-fullscreen mode. CSS rule on body.fullscreen-preview hides
 *  toolbar / rail / folder tree / list / statusbar and expands the viewer to
 *  the full viewport. Exits via second toggle, Esc, or the floating ✕. */
export function toggleFullscreenPreview(): void {
    const body = document.body;
    const on = !body.classList.contains("fullscreen-preview");
    body.classList.toggle("fullscreen-preview", on);
    let exit = document.getElementById("mv-exit-fullscreen");
    if (on) {
        if (!exit) {
            exit = document.createElement("button");
            exit.id = "mv-exit-fullscreen";
            exit.textContent = "✕";
            exit.title = "Exit fullscreen (Esc or double-tap)";
            exit.style.cssText = "position:fixed;top:8px;right:8px;z-index:6000;width:36px;height:36px;border-radius:50%;border:none;background:rgba(0,0,0,0.55);color:#fff;font-size:18px;cursor:pointer;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.4);";
            exit.addEventListener("click", () => toggleFullscreenPreview());
            document.body.appendChild(exit);
        }
        // Esc handler — installed once, reads the class on each press.
        if (!(window as any).__mvFullscreenEsc) {
            const onEsc = (e: KeyboardEvent) => {
                if (e.key === "Escape" && document.body.classList.contains("fullscreen-preview")) {
                    e.preventDefault();
                    toggleFullscreenPreview();
                }
            };
            document.addEventListener("keydown", onEsc, true);
            (window as any).__mvFullscreenEsc = onEsc;
        }
    } else {
        exit?.remove();
    }
}

/** Kept as an exported no-op for back-compat with app.ts wiring. The
 *  state-subscribe model is gone; list now drives the viewer directly. */
export function initViewer(): void { /* no-op: list owns focus */ }

// Zoom is persisted across messages via localStorage
const ZOOM_KEY = "mailx-preview-zoom";
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3.0;
const ZOOM_STEP = 0.1;
// 1.15 default: smaller than browser-default text felt cramped per user
// feedback 2026-05-20. Ctrl+= / Ctrl+- / Ctrl+wheel / right-click menu all
// override and persist, so the default only matters on first-ever launch or
// after a WebView2 profile wipe.
let previewZoom = clampZoom(parseFloat(localStorage.getItem(ZOOM_KEY) || "1.15"));

function clampZoom(z: number): number {
    if (!Number.isFinite(z) || z <= 0) return 1;
    return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Math.round(z * 100) / 100));
}

function applyZoom(doc: Document): void {
    // Zoom lives on <html>, not <body>, because WebView2/Chromium's scroll
    // container is the root element — body.style.zoom leaves the scroll
    // container out of sync with the zoomed content, breaking scrollbar
    // display and wheel scrolling. documentElement.style.zoom keeps them
    // aligned so the iframe scrolls normally at any zoom level.
    if (doc.documentElement) (doc.documentElement.style as any).zoom = String(previewZoom);
}

function setZoom(z: number, doc: Document): void {
    previewZoom = clampZoom(z);
    localStorage.setItem(ZOOM_KEY, String(previewZoom));
    applyZoom(doc);
}

/** Install preview iframe controls: key forwarding to parent, Ctrl+wheel zoom,
 *  keyboard zoom shortcuts (Ctrl+= / Ctrl+- / Ctrl+0), and the right-click menu. */
/** Run AI translate on `text` and show result in a small modal. Disabled
 *  by default — user enables via Settings (translateEnabled in
 *  AutocompleteSettings). When disabled, the modal explains how to enable. */
async function translateAndShow(text: string): Promise<void> {
    if (!text.trim()) return;
    const status = document.getElementById("status-sync");
    if (status) status.textContent = "Translating…";

    const overlay = document.createElement("div");
    overlay.style.cssText = "position:fixed;inset:0;background:rgba(0,0,0,0.4);z-index:10000;display:flex;align-items:center;justify-content:center;";
    const modal = document.createElement("div");
    modal.style.cssText = "background:var(--bg, #fff);color:var(--fg, #000);border:1px solid var(--border, #ccc);border-radius:6px;width:560px;max-width:90vw;max-height:70vh;display:flex;flex-direction:column;box-shadow:0 4px 24px rgba(0,0,0,0.3);";
    const header = document.createElement("div");
    header.style.cssText = "padding:10px 14px;border-bottom:1px solid var(--border, #ddd);font-weight:600;display:flex;justify-content:space-between;align-items:center;";
    header.innerHTML = `<span>Translation</span><button style="cursor:pointer;border:0;background:transparent;font-size:16px;" aria-label="Close">×</button>`;
    const body = document.createElement("div");
    body.style.cssText = "flex:1;overflow:auto;padding:12px 14px;white-space:pre-wrap;font-size:13px;line-height:1.45;";
    body.textContent = "Working…";
    modal.appendChild(header);
    modal.appendChild(body);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
    const close = () => overlay.remove();
    header.querySelector("button")?.addEventListener("click", close);
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close(); });
    document.addEventListener("keydown", function onKey(e: KeyboardEvent) {
        if (e.key === "Escape") { document.removeEventListener("keydown", onKey); close(); }
    });

    try {
        const { aiTransform } = await import("../lib/api-client.js");
        const r = await aiTransform({ action: "translate", text, targetLang: "en" });
        if (r.text) {
            body.textContent = r.text;
            if (status) status.textContent = "";
        } else {
            body.innerHTML = `<div style="color:var(--muted, #888);">No result.</div>` +
                `<div style="margin-top:8px;font-size:12px;color:var(--muted, #888);">${r.reason || ""}</div>` +
                `<div style="margin-top:14px;font-size:12px;">Enable AI translate in Settings → AI features (off by default).</div>`;
            if (status) status.textContent = `Translate: ${r.reason || "no result"}`;
        }
    } catch (err: any) {
        body.textContent = `Error: ${err?.message || String(err)}`;
        if (status) status.textContent = `Translate error: ${err?.message || ""}`;
    }
}

function installPreviewControls(iframe: HTMLIFrameElement): void {
    const attach = () => {
        const doc = iframe.contentDocument;
        if (!doc) return;

        applyZoom(doc);

        doc.addEventListener("keydown", (e) => {
            const target = e.target as HTMLElement | null;
            if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) return;
            // Zoom is iframe-local — handle here, don't forward.
            if (e.ctrlKey && (e.key === "=" || e.key === "+")) { e.preventDefault(); setZoom(previewZoom + ZOOM_STEP, doc); return; }
            if (e.ctrlKey && e.key === "-") { e.preventDefault(); setZoom(previewZoom - ZOOM_STEP, doc); return; }
            if (e.ctrlKey && e.key === "0") { e.preventDefault(); setZoom(1, doc); return; }
            // Ctrl+A is preview-local too: select the LETTER's text so it can
            // be copied. Forwarding it let the list's select-all-rows grab it
            // even with focus in the preview (Bob 2026-07-18). Don't forward,
            // don't preventDefault — the browser's native select-all inside
            // the iframe selects exactly this document.
            if (e.ctrlKey && !e.altKey && !e.metaKey && (e.key === "a" || e.key === "A")) return;
            // Forward EVERY keydown to the parent — no duplicated hotkey list.
            // If the parent's handler calls preventDefault (because it owns the
            // shortcut), dispatchEvent returns false, and we preventDefault on
            // the iframe side too so the browser doesn't ALSO act on it
            // (Ctrl+N otherwise pops a new browser window in some hosts).
            // Single source of truth = app.ts hotkey handlers. Plain typing in
            // the email body — letters, etc. — propagates with no parent
            // handler matching, so dispatchEvent returns true and the iframe
            // event is left alone.
            const synth = new KeyboardEvent("keydown", {
                key: e.key, code: e.code,
                ctrlKey: e.ctrlKey, shiftKey: e.shiftKey, altKey: e.altKey, metaKey: e.metaKey,
                bubbles: true, cancelable: true,
            });
            const allowDefault = document.dispatchEvent(synth);
            if (!allowDefault) e.preventDefault();
        });

        doc.addEventListener("wheel", (e) => {
            if (!e.ctrlKey) return;
            e.preventDefault();
            setZoom(previewZoom + (e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP), doc);
        }, { passive: false });

        // dblclick fullscreen toggle moved into the iframe's inline script
        // so it's wired by the time the document loads — the parent-side
        // contentDocument listener was racing with the load event and
        // sometimes never attached.

        // Link interception lives in the iframe's own inline <script> (see
        // wrapHtmlBody). That script runs under a CSP nonce so email-body
        // scripts stay blocked while ours forwards taps to the parent frame.

        // Body-context menu was previously built here on doc.contextmenu, but
        // the inline iframe script (wrapHtmlBody) now postMessages the request
        // to the parent unconditionally — see showPreviewContextMenu in app.ts
        // which calls showPreviewBodyMenu below. This route works on every
        // host; the doc-level handler missed cases where WebView2's native
        // menu fired before our parent listener got installed.
    };
    if (iframe.contentDocument?.readyState === "complete") attach();
    else iframe.addEventListener("load", attach, { once: true });
}

export function clearViewer(): void {
    currentMessage = null;
    currentAccountId = "";
    lastEnvelope = null;
    showMessageGeneration++;
    const headerEl = document.getElementById("mv-header") as HTMLElement;
    const bodyEl = document.getElementById("mv-body") as HTMLElement;
    const attEl = document.getElementById("mv-attachments") as HTMLElement;
    if (bodyEl) bodyEl.innerHTML = `<div class="mv-empty">Select a message to read</div>`;
    if (headerEl) headerEl.hidden = true;
    if (attEl) attEl.hidden = true;
}

// Viewer-side guard on the viewer-must-clear rule: when the message ON
// DISPLAY is removed (draft discarded from compose, EXPUNGE from another
// client), clear the pane directly. The list's remove-stale flow also
// clears via focus-handoff — but only when the removed row is in the
// CURRENT view; a draft previewed earlier (or any cross-folder removal)
// no-ops there and the pane kept showing the dead message (Bob 2026-07-18
// "I discarded a message but it is still showing in preview").
subscribeStore("*", (ev: any) => {
    if (ev?.kind !== "messageRemoved") return;
    if (!currentMessage) return;
    const folderMatches = ev.folderId == null || currentMessage.folderId == null
        || ev.folderId === currentMessage.folderId;
    if (ev.accountId === currentAccountId && ev.uid === currentMessage.uid && folderMatches) {
        clearViewer();
    }
});

/** Render the message identified by (accountId, uid, folderId). The
 *  list passes the row's existing envelope as `envelope` so the header +
 *  preview snippet paint *immediately*, before the body fetch returns —
 *  that's the "no Fetching… empty pane" guarantee. On retry, we reuse
 *  the most recently provided envelope. */
export async function showMessage(accountId: string, uid: number, folderId?: number, specialUse?: string, isRetry = false, envelope?: ListMessage): Promise<void> {
    const gen = ++showMessageGeneration;
    if (!isRetry) retryCount = 0;
    const headerEl = document.getElementById("mv-header") as HTMLElement;
    const bodyEl = document.getElementById("mv-body") as HTMLElement;
    const attEl = document.getElementById("mv-attachments") as HTMLElement;

    // Same-message re-show guard. The list rebuilds on folderCountsChanged
    // (sync events, IDLE, flag updates) and `restoreSelection()` then calls
    // `focusRow()` → `viewerShow()` on the row whose uid matches. If we
    // re-paint here, `bodyEl.innerHTML = …` later in this function destroys
    // the iframe and resets the user's scroll position to top. Skip that
    // entirely when the message identity hasn't changed and we've got a
    // live iframe to keep. Header refresh is harmless (no scroll inside)
    // and keeps flag/read state visually current. Retry path bypasses the
    // guard so transient body fetches still get their delayed paint.
    //
    // Identity check: prefer the stable `uuid`. (uid, folderId) is NOT a
    // reliable identity — uid is per-folder, so a cross-folder search can
    // surface two different messages with the same uid, and a result whose
    // folderId is omitted made the guard mis-fire: the header refreshed to
    // the clicked message but the body kept the previously-shown message
    // (Bob 2026-05-18: the .eml shown "does not match the header"). Fall
    // back to (folderId, uid) only when a uuid isn't available on both
    // sides (e.g. thread-popup envelopes, which carry no uuid) — and then
    // ONLY with a known folderId, since (account, folder, uid) is a real
    // identity but (account, uid) alone is not. A missing folderId is
    // treated as "can't confirm → different message", forcing a re-render:
    // showing a stale unrelated letter (Bob 2026-05-22: "clicked a letter
    // but it's still showing one I'd been looking at") is far worse than a
    // re-render's scroll reset.
    const sameMessage = !!currentMessage && currentAccountId === accountId && (
        (envelope?.uuid && currentMessage.uuid)
            ? envelope.uuid === currentMessage.uuid
            : (folderId !== undefined
                && currentMessage.uid === uid
                && currentMessage.folderId === folderId)
    );
    if (!isRetry && sameMessage && bodyEl.querySelector("iframe")) {
        if (envelope) {
            lastEnvelope = envelope;
            try { renderHeaderFromEnvelope(headerEl, envelope); headerEl.hidden = false; } catch { /* */ }
        }
        return;
    }

    // Click-to-preview timing — answers "why is this slow?" with numbers.
    // Marks: enter / cache-hit-or-miss / IPC-begin / IPC-done / iframe-set /
    // iframe-loaded. Without these the symptom "previews very slow" is
    // unmeasurable.
    const _previewT0 = performance.now();
    const _ptick = (label: string): void => {
        const line = `[preview ${(performance.now() - _previewT0).toFixed(0).padStart(5)} ms] ${label} ${accountId}/uid=${uid}`;
        console.log("  " + line);
        try { (window as any).mailxapi?.logClientEvent?.(line); } catch { /* */ }
    };
    _ptick("showMessage entry");

    // Clear any "current message" identity at the START of a new render —
    // before a single byte is fetched. Action buttons (Source, Edit Draft,
    // attachments) read from `currentMessage` and must NOT serve the
    // previous message's data while this one is still loading. The
    // closures they had pointed at the old message; clearing the shared
    // state forces them to read whatever this render eventually commits.
    currentMessage = null;
    currentAccountId = "";
    // Hide action buttons that depend on the message body — they'll be
    // restored when the new message data arrives.
    const srcBtn = document.getElementById("mv-view-source") as HTMLButtonElement | null;
    if (srcBtn) srcBtn.hidden = true;
    const editBtn = document.getElementById("mv-edit-draft") as HTMLButtonElement | null;
    if (editBtn) editBtn.hidden = true;

    // Envelope-first render: the row the user just clicked already has the
    // subject / from / to / cc / date / preview in the message-state. Use
    // that to populate the header + a snippet placeholder IMMEDIATELY so
    // tapping a message never shows just "Fetching message body..." with
    // nothing actionable. The full getMessage() call (which might block on
    // a slow IMAP body fetch) only fills in the body and attachments.
    const cached = (envelope && envelope.uid === uid && (envelope.accountId || accountId) === accountId)
        ? envelope
        : (lastEnvelope && lastEnvelope.uid === uid && (lastEnvelope.accountId || accountId) === accountId ? lastEnvelope : null);
    if (envelope) lastEnvelope = envelope;

    // Cache lookup BEFORE any placeholder paint. If this message's parsed
    // body is in WebView memory (recently viewed), skip the IPC entirely —
    // render synchronously below.
    const cachedMsg = parsedCacheGet(accountId, folderId, uid);

    if (cached) {
        try { renderHeaderFromEnvelope(headerEl, cached); } catch { /* */ }
        // 2026-05-13: if the envelope has a non-empty `preview`, show it as
        // a miniature body until the parsed HTML arrives. If it's empty
        // (common for mailing-list / quoted-reply messages), show an
        // explicit "Loading body…" so the user never sees a totally blank
        // pane and thinks the click didn't register. Earlier wording chose
        // silence on the theory that "Loading…" is a lie when fast — but
        // an empty pane is a worse lie when the parse is slow.
        if (!cachedMsg) {
            const previewText = (cached.preview || "").trim();
            bodyEl.innerHTML = previewText
                ? `<div class="mv-preview-placeholder">${escapeHtml(previewText)}</div>`
                : `<div class="mv-empty">Loading body…</div>`;
        }
    } else if (!cachedMsg) {
        // No envelope and no cache — show a loading indicator instead of
        // an empty pane. The IPC return will paint over it.
        bodyEl.innerHTML = `<div class="mv-empty">Loading body…</div>`;
        headerEl.hidden = true;
    }
    attEl.hidden = true;

    try {
        if (cachedMsg) _ptick("parsedCache HIT — sync render");
        else _ptick("parsedCache MISS — IPC begin");
        const msg = cachedMsg ? cachedMsg : await getMessage(accountId, uid, false, folderId);
        if (!cachedMsg) _ptick(`IPC done (cached=${(msg as any).cached !== false}, ${(msg as any).bodyHtml?.length || 0} bytes html)`);
        // Stale response — a newer showMessage was called while we were fetching
        if (gen !== showMessageGeneration) return;

        // User clicked Allow earlier this session — treat as unblocked
        // regardless of what the daemon currently reports. Covers the
        // race where the allowlist persist is still in flight.
        if (isSessionAllowed(accountId, uid) && (msg as any).hasRemoteContent) {
            (msg as any).hasRemoteContent = false;
        }

        // Body not yet on disk — service kicked off a background fetch and
        // will emit `bodyAvailable` when the bytes land. Show envelope-only
        // (header + preview snippet) and re-render on the event. No timer,
        // no client-side retry; the reconciler retries server-side.
        if ((msg as any).cached === false) {
            try { renderHeaderFromEnvelope(headerEl, msg as any); headerEl.hidden = false; } catch { /* */ }
            // Body genuinely missing from local store — the reconciler is
            // fetching it from IMAP/Gmail. Show preview as a miniature
            // body if available, otherwise a clear "Fetching from server…"
            // so a slow fetch isn't indistinguishable from a hung pane.
            const previewText = ((msg as any).preview || cached?.preview || "").trim();
            // Wait indicator: render an elapsed-time counter alongside the
            // placeholder. A still-counting timer means the fetch is alive
            // but slow; a stalled count means the bodyAvailable event isn't
            // firing (folder_id corruption, iflow desync, Gmail API 429,
            // etc.). Without this, "Fetching body from server…" looked the
            // same at 1 s as at 5 min, and a real wedge was indistinguishable
            // from a normal slow Dovecot read (Bob 2026-05-24).
            const waitStart = Date.now();
            const indicatorHtml = `<span class="mv-wait-elapsed" data-start="${waitStart}">(0s)</span>`;
            bodyEl.innerHTML = previewText
                ? `<div class="mv-preview-placeholder">${escapeHtml(previewText)}<div class="mv-tear-line" aria-label="snippet ends here, full message loading"><span>✂ snippet — fetching full message ✂</span></div><div class="mv-wait-line">Fetching body from server… ${indicatorHtml}</div></div>`
                : `<div class="mv-empty">Fetching body from server… ${indicatorHtml}</div>`;
            const captureGen = gen;
            // 1 Hz tick to update the elapsed counter. Stops automatically
            // when (a) bodyAvailable arrives, (b) generation advances (user
            // navigated), or (c) the element vanishes from the DOM.
            const tick = setInterval(() => {
                if (captureGen !== showMessageGeneration) { clearInterval(tick); return; }
                const span = bodyEl.querySelector<HTMLSpanElement>(".mv-wait-elapsed");
                if (!span) { clearInterval(tick); return; }
                const secs = Math.round((Date.now() - waitStart) / 1000);
                span.textContent = secs >= 60
                    ? `(${Math.floor(secs / 60)}m ${secs % 60}s)`
                    : `(${secs}s)`;
                // After 30 s a still-counting timer is suspicious — surface
                // a hint so the user knows the daemon should be looked at.
                if (secs === 30) span.classList.add("mv-wait-slow");
            }, 1000);
            // Subscribe via Store bus (the post-refactor surface). The
            // reconciler publishes `bodyAvailable` on `message:<uuid>` (or
            // `folder:<id>` when uuid isn't available); the wildcard catches
            // both. Match by account+uid because the viewer keys off those.
            const off = subscribeStore("*", (ev: any) => {
                if (ev.kind !== "bodyAvailable") return;
                if (ev.accountId !== accountId || ev.uid !== uid) return;
                if (captureGen !== showMessageGeneration) { off(); clearInterval(tick); return; }
                off();
                clearInterval(tick);
                showMessage(accountId, uid, folderId, specialUse, true, envelope || cached || undefined).catch(() => { /* */ });
            });
            return;
        }
        // Web/Android service path: a failed body fetch comes back as
        // bodyError on the message itself (the desktop daemon signals
        // cached=false + a bodyAvailable event instead). Render it as a
        // fetch failure — NOT the "no body text — only a subject"
        // placeholder, which claims the message is empty when it isn't
        // (Bob 2026-07-03) — and do NOT parsedCachePut, so reopening the
        // message retries instead of pinning the failure in cache.
        if ((msg as any).bodyError && !(msg as any).bodyHtml && !(msg as any).bodyText) {
            try { renderHeaderFromEnvelope(headerEl, msg as any); headerEl.hidden = false; } catch { /* */ }
            const transient = (msg as any).bodyErrorTransient;
            bodyEl.innerHTML = `<div class="mv-system-message mv-system-error">
                <div class="mv-system-tag">mailx</div>
                <div class="mv-system-title">Body fetch failed</div>
                <div class="mv-system-body">${escapeHtml((msg as any).bodyError)}<br><span style="color:var(--color-text-muted);font-size:0.9em">${transient ? "Reopen this message to retry." : "The server reports this message no longer exists (deleted or moved by another client)."}</span></div>
            </div>`;
            currentMessage = msg;
            currentAccountId = accountId;
            return;
        }
        // Cache the fully-parsed result so re-clicking this message paints
        // synchronously next time (no IPC, no parse). Cache is bounded
        // (PARSED_CACHE_LIMIT) and skipped if we got here via cache hit.
        if (!cachedMsg) parsedCachePut(accountId, folderId, uid, msg);
        currentMessage = msg;
        currentAccountId = accountId;

        // Mark as read — gated by user prefs:
        //   - mailx-automark-read (default "true"): if "false", never auto-mark
        //   - mailx-automark-delay (default "2"): seconds to wait before
        //     marking. Lets the user click through messages quickly without
        //     marking ones they didn't actually read. The timer is tied to
        //     showMessageGeneration; navigating to another message advances
        //     the generation and cancels the pending mark.
        if (!seenOf(msg)) {
            let enabled = true;
            let delaySec = 2;
            try {
                enabled = localStorage.getItem("mailx-automark-read") !== "false";
                const d = parseFloat(localStorage.getItem("mailx-automark-delay") || "2");
                if (Number.isFinite(d) && d >= 0) delaySec = d;
            } catch { /* private mode — defaults */ }
            if (enabled) {
                const captureGen = gen;
                const apply = () => {
                    // Stale: user moved on before the timer fired.
                    if (captureGen !== showMessageGeneration) return;
                    setSeen(msg, true);
                    updateFlags(accountId, uid, msg.flags, (msg as any).folderId);
                    // Mirror to message-state + row so list display flips.
                    try { stateUpdateFlags(accountId, uid, msg.flags); } catch { /* */ }
                    try { setRowSeen(accountId, uid, true); } catch { /* */ }
                };
                if (delaySec === 0) apply();
                else setTimeout(apply, delaySec * 1000);
            }
        }

        // Header
        headerEl.hidden = false;
        const fromEl = headerEl.querySelector(".mv-from")!;
        const toEl = headerEl.querySelector(".mv-to")!;
        fromEl.textContent = formatAddr(msg.from);
        let toLine = `To: ${msg.to.map(formatAddr).join(", ")}`;
        if (msg.cc?.length) toLine += `  Cc: ${msg.cc.map(formatAddr).join(", ")}`;
        // Always-visible Delivered-To line — shown when present and not already
        // covered by the To/Cc list. Critical for accounts with multiple aliases
        // where you need to see which one received the message at a glance.
        const toAddrs = (msg.to || []).map((a: { address: string }) => a.address.toLowerCase());
        const ccAddrs = (msg.cc || []).map((a: { address: string }) => a.address.toLowerCase());
        const dt = (msg.deliveredTo || "").toLowerCase();
        if (msg.deliveredTo && !toAddrs.includes(dt) && !ccAddrs.includes(dt)) {
            toLine += `  Delivered-To: ${msg.deliveredTo}`;
        }
        toEl.textContent = toLine;
        headerEl.querySelector(".mv-subject")!.textContent = msg.subject;
        document.dispatchEvent(new CustomEvent("mailx-message-shown", { detail: { accountId } }));

        // Right-click on email addresses in header: copy name, copy address,
        // copy both, add to contacts, plus reply actions for the whole message.
        for (const el of [fromEl, toEl]) {
            el.addEventListener("contextmenu", (e: Event) => {
                e.preventDefault();
                const me = e as MouseEvent;
                const items: MenuItem[] = [];
                const addrs = el === fromEl ? [msg.from] : [...(msg.to || []), ...(msg.cc || [])];
                for (const addr of addrs) {
                    if (!addr?.address) continue;
                    const name = addr.name || "";
                    const both = name ? `${name} <${addr.address}>` : addr.address;
                    if (name) {
                        items.push({ label: `Copy name: ${name}`, action: () => navigator.clipboard.writeText(name) });
                    }
                    items.push({ label: `Copy address: ${addr.address}`, action: () => navigator.clipboard.writeText(addr.address) });
                    if (name) {
                        items.push({ label: `Copy both: ${both}`, action: () => navigator.clipboard.writeText(both) });
                    }
                    items.push({
                        label: `Open in contacts: ${addr.address}`,
                        action: async () => {
                            const { openAddressBook } = await import("./address-book.js");
                            await openAddressBook(addr.address);
                        },
                    });
                    items.push({
                        label: `Add to contacts: ${addr.address}`,
                        action: async () => {
                            await showAddContactDialog(name, addr.address);
                        },
                    });
                    // "Add to preferred" — separate path: writes to
                    // contacts.jsonc#preferred[] with an optional source tag.
                    // Distinct from "Add to contacts" which goes into the DB +
                    // pushes to Google. Preferred entries rank higher in
                    // autocomplete and survive Google sync's churn.
                    items.push({
                        label: `Add to preferred: ${addr.address}`,
                        action: async () => {
                            const tag = prompt("Tag (e.g. work, family, vendor) — leave blank for default:", "");
                            if (tag === null) return; // user cancelled
                            try {
                                await addPreferredContact({ name, email: addr.address, source: tag.trim() || undefined });
                                const status = document.getElementById("status-sync");
                                if (status) status.textContent = `Added to preferred: ${addr.address}${tag ? ` [${tag}]` : ""}`;
                            } catch (e: any) {
                                alert(`Couldn't add to preferred: ${e?.message || e}`);
                            }
                        },
                    });
                    // Mark / unmark sender as priority — visual highlight in
                    // the message list (gold left bar + bold sender name);
                    // strongest while unread, fades when read. Stored as
                    // `priority: true` on the contacts.jsonc preferred[]
                    // entry (auto-creates the entry if it doesn't exist).
                    items.push({
                        label: `★ Mark sender as priority: ${addr.address}`,
                        action: async () => {
                            try {
                                const { setPrioritySender } = await import("../lib/api-client.js");
                                await setPrioritySender(addr.address, true, name);
                                const { refreshPriorityIndex } = await import("./message-list.js");
                                await refreshPriorityIndex();
                                const status = document.getElementById("status-sync");
                                if (status) status.textContent = `Priority: ${addr.address}`;
                            } catch (e: any) {
                                alert(`Couldn't mark priority: ${e?.message || e}`);
                            }
                        },
                    });
                    items.push({ label: "", action: () => {}, separator: true });
                }
                items.push({ label: "Reply", action: () => document.dispatchEvent(new CustomEvent("mailx-compose", { detail: { mode: "reply" } })) });
                items.push({ label: "Reply All", action: () => document.dispatchEvent(new CustomEvent("mailx-compose", { detail: { mode: "replyAll" } })) });
                items.push({ label: "Forward", action: () => document.dispatchEvent(new CustomEvent("mailx-compose", { detail: { mode: "forward" } })) });
                showContextMenu(me.clientX, me.clientY, items);
            });
        }
        headerEl.querySelector(".mv-date")!.textContent = new Date(msg.date).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });

        // Unsubscribe button (upper right of header).
        //   - One-Click (RFC 8058): POST via service; show result in status bar.
        //   - Plain HTTPS URL: open externally for user confirmation.
        //   - mailto: open a pre-filled compose so the reply uses the right account.
        const unsubBtn = document.getElementById("mv-unsubscribe") as HTMLAnchorElement;
        const httpUrl = (msg as any).listUnsubscribeHttp || "";
        const mailUrl = (msg as any).listUnsubscribeMail || "";
        const oneClick = !!(msg as any).listUnsubscribeOneClick;
        const anyUrl = httpUrl || mailUrl || msg.listUnsubscribe || "";
        if (unsubBtn) {
            if (anyUrl) {
                unsubBtn.hidden = false;
                unsubBtn.textContent = "Unsubscribe";
                unsubBtn.removeAttribute("title");
                unsubBtn.href = httpUrl || mailUrl || "#";
                unsubBtn.onclick = async (e) => {
                    e.preventDefault();
                    const status = document.getElementById("status-sync");
                    if (httpUrl && oneClick) {
                        if (status) status.textContent = "Unsubscribing…";
                        try {
                            const result = await unsubscribeOneClick(httpUrl);
                            if (status) {
                                status.textContent = result.ok
                                    ? `Unsubscribed (${result.status} ${result.statusText})`
                                    : `Unsubscribe failed: ${result.status} ${result.statusText}`;
                            }
                        } catch (err: any) {
                            if (status) status.textContent = `Unsubscribe error: ${err?.message || err}`;
                        }
                        return;
                    }
                    if (httpUrl) {
                        const api = (window as any).mailxapi;
                        if (api?.openExternal) api.openExternal(httpUrl);
                        else window.open(httpUrl, "_blank", "noopener,noreferrer");
                        return;
                    }
                    if (mailUrl) {
                        const m = mailUrl.match(/^mailto:([^?]*)(?:\?(.*))?$/i);
                        const to = m?.[1] ? decodeURIComponent(m[1]) : "";
                        const qs = new URLSearchParams(m?.[2] || "");
                        // 2026-05-13: when the unsubscribe-side specifies no
                        // subject/body in the mailto, pre-fill from the
                        // ORIGINAL message so the human (or filter) at the
                        // other end sees what list this is for. Bare
                        // mailto:unsub+<token>@... gives the recipient an
                        // opaque token and no human-readable context.
                        const origFrom = msg.from
                            ? (msg.from.name ? `${msg.from.name} <${msg.from.address}>` : msg.from.address)
                            : "";
                        const origSubject = msg.subject || "";
                        const origDate = msg.date
                            ? new Date(msg.date).toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })
                            : "";
                        const subject = qs.get("subject")
                            || (origSubject ? `Unsubscribe: ${origSubject}` : "Unsubscribe");
                        const inlineBody = qs.get("body") || "";
                        const escape = (s: string) => s
                            .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
                        const contextLines: string[] = [];
                        if (origFrom)    contextLines.push(`From: ${escape(origFrom)}`);
                        if (origSubject) contextLines.push(`Subject: ${escape(origSubject)}`);
                        if (origDate)    contextLines.push(`Date: ${escape(origDate)}`);
                        const contextBlock = contextLines.length
                            ? `<p>—<br>${contextLines.join("<br>")}</p>`
                            : "";
                        const bodyHtml = inlineBody
                            ? `<p>${escape(inlineBody)}</p>${contextBlock}`
                            : `<p>Please unsubscribe me from this list.</p>${contextBlock}`;
                        const init = {
                            mode: "new",
                            accountId: currentAccountId,
                            to: to ? [{ name: "", address: to }] : [] as { name: string; address: string }[],
                            cc: [] as { name: string; address: string }[],
                            subject,
                            bodyHtml,
                            inReplyTo: "",
                            references: [] as string[],
                            accounts: [] as { id: string; name: string; email: string }[],
                        };
                        sessionStorage.setItem("composeInit", JSON.stringify(init));
                        document.dispatchEvent(new CustomEvent("mailx-compose", { detail: { mode: "new" } }));
                    }
                };
            } else {
                unsubBtn.hidden = true;
            }
        }

        // View Thread button — opens the thread popup from the message list
        // so the user can see all messages in the conversation. Works from
        // the viewer even when thread-grouping is off.
        const threadBtn = document.getElementById("mv-view-thread") as HTMLButtonElement;
        if (threadBtn) {
            const tid = (msg as any).threadId || "";
            if (tid) {
                threadBtn.hidden = false;
                threadBtn.onclick = async () => {
                    const { showThreadPopup } = await import("./message-list.js");
                    await showThreadPopup(threadBtn, { accountId, threadId: tid });
                };
            } else {
                threadBtn.hidden = true;
            }
        }

        // View Source button. CRITICAL: handler reads from the
        // `currentMessage` singleton, NOT a captured `msg` closure. If the
        // user clicks while a different message is mid-render the singleton
        // is null (cleared at top of showMessage) so the click is a no-op
        // — instead of returning the previous message's path. The principle
        // (per Bob): there is one source-of-truth identity for "what's
        // shown" and every action reads from it at click time.
        const srcBtn = document.getElementById("mv-view-source") as HTMLButtonElement;
        if (srcBtn) {
            if (msg.emlPath) {
                srcBtn.hidden = false;
                srcBtn.title = `${msg.emlPath}\n(click to copy path, double-click to open in default text editor)`;
                srcBtn.onclick = () => {
                    const path = currentMessage?.emlPath;
                    if (!path) return;
                    navigator.clipboard.writeText(path).then(() => {
                        const status = document.getElementById("status-sync");
                        if (status) status.textContent = `Path copied: ${path}`;
                    }).catch(() => {
                        prompt("EML file path:", path);
                    });
                };
                // Double-click opens the .eml in the OS default *text*
                // editor (Notepad / TextEdit / $EDITOR). Distinct from
                // the file's associated app, which for .eml is usually
                // Outlook — not what the user wants when inspecting the
                // raw MIME. Bob 2026-05-11: "if anything it should open
                // up the default .txt editor."
                srcBtn.ondblclick = (e) => {
                    e.preventDefault();
                    const path = currentMessage?.emlPath;
                    if (!path) return;
                    import("../lib/api-client.js").then(m => m.openInTextEditor(path)).then((r: any) => {
                        const status = document.getElementById("status-sync");
                        if (status) {
                            status.textContent = r?.ok
                                ? `Opened in ${r.opener}: ${path}`
                                : `Open failed: ${r?.reason || "unknown"}`;
                        }
                    }).catch((e: any) => {
                        console.error("[mv] openInTextEditor failed:", e);
                    });
                };
            } else {
                srcBtn.hidden = true;
                srcBtn.onclick = null;
                srcBtn.ondblclick = null;
            }
        }

        // Drafts and Outbox messages open directly into compose. The Edit
        // Draft button stays as a fallback (and to keep the manual flow
        // discoverable), but selecting a draft no longer requires the user
        // to click it — opening a draft and editing it are the same action.
        // Guard: don't re-open compose for the SAME draft on every viewer
        // re-render (msg-state events fire on count updates etc.). And
        // don't auto-open while a compose overlay is already up — the user
        // is mid-edit on something and shouldn't lose focus to a new window.
        const editBtn = document.getElementById("mv-edit-draft") as HTMLButtonElement;
        // Detect drafts by FLAG, not just folder. A message marked \Draft
        // by the server is editable regardless of which folder the user
        // is currently viewing (e.g., clicking a draft from a thread in
        // INBOX, or from "All Inboxes" where currentSpecialUse is empty).
        // Bob 2026-05-12: "How do I get into edit mode?"
        const msgFlags: string[] = Array.isArray(msg?.flags) ? msg.flags : [];
        const flagged = msgFlags.includes("\\Draft");
        const isDraft = flagged || specialUse === "drafts" || specialUse === "outbox";
        const openDraftInCompose = (cm: any) => {
            const init = {
                mode: "draft",
                accountId: currentAccountId,
                to: cm.to || [],
                cc: cm.cc || [],
                subject: cm.subject || "",
                bodyHtml: cm.bodyHtml || "",
                inReplyTo: cm.inReplyTo || "",
                references: cm.references || [],
                accounts: [] as { id: string; name: string; email: string }[],
                draftUid: cm.uid,
                draftFolderId: cm.folderId,
                draftId: cm.mailxDraftId || "",
            };
            sessionStorage.setItem("composeInit", JSON.stringify(init));
            document.dispatchEvent(new CustomEvent("mailx-compose", { detail: { mode: "draft" } }));
        };
        if (editBtn) {
            if (isDraft) {
                editBtn.hidden = false;
                editBtn.textContent = specialUse === "outbox" ? "Edit & Send" : "Edit Draft";
                editBtn.onclick = () => { const cm = currentMessage; if (cm) openDraftInCompose(cm); };
            } else {
                editBtn.hidden = true;
                editBtn.onclick = null;
            }
        }
        // Drafts render in the preview pane like any other message. The
        // user opens them for editing explicitly via the "Edit Draft"
        // button or by double-clicking the row (Bob 2026-05-23: "if I look
        // at a letter in drafts it creates popup immediately. It shouldn't
        // do that unless I ask — I should just see the preview").

        // Details toggle — show extra headers (Delivered-To, Return-Path, Message-ID, etc.)
        const detailsEl = document.getElementById("mv-details") as HTMLElement;
        const detailsBtn = document.getElementById("mv-toggle-details") as HTMLButtonElement;
        if (detailsEl && detailsBtn) {
            // Q56: every row gets a Copy button so paths / IDs can be pasted
            // into accounts.jsonc hints or bug reports.
            const row = (label: string, value: string) =>
                `<div class="mv-details-row"><span class="mv-details-label">${label}:</span> <span class="mv-details-value">${escapeText(value)}</span> <button type="button" class="mv-details-copy" data-copy="${escapeText(value).replace(/"/g, "&quot;")}" title="Copy">⧉</button></div>`;
            const lines: string[] = [];
            if (msg.deliveredTo) lines.push(row("Delivered-To", msg.deliveredTo));
            if (msg.returnPath) lines.push(row("Return-Path", msg.returnPath));
            if (msg.messageId) lines.push(row("Message-ID", msg.messageId));
            if (msg.listUnsubscribe) lines.push(row("Unsubscribe", msg.listUnsubscribe));
            if (msg.emlPath) lines.push(row("EML file", msg.emlPath));
            lines.push(row("Account", accountId));
            lines.push(row("UID", `${msg.uid} (folder ${msg.folderId})`));
            detailsEl.innerHTML = lines.join("");
            detailsEl.hidden = true;
            detailsBtn.textContent = "Details";
            detailsBtn.onclick = () => {
                detailsEl.hidden = !detailsEl.hidden;
                detailsBtn.textContent = detailsEl.hidden ? "Details" : "\u2713 Details";
            };
            // Wire copy buttons.
            detailsEl.querySelectorAll<HTMLButtonElement>(".mv-details-copy").forEach(btn => {
                btn.addEventListener("click", async (e) => {
                    e.stopPropagation();
                    const val = btn.dataset.copy || "";
                    try {
                        await navigator.clipboard.writeText(val);
                        btn.textContent = "✓";
                        setTimeout(() => { btn.textContent = "⧉"; }, 1500);
                    } catch {
                        prompt("Copy:", val);
                    }
                });
            });
        }

        // Remote content banner (collapsible dropdown with sender/recipient details)
        bodyEl.innerHTML = "";
        if (msg.hasRemoteContent) {
            const senderAddr = msg.from?.address || "";
            const senderName = msg.from?.name || "";
            const senderDomain = senderAddr.split("@")[1] || "";
            const deliveredTo = msg.deliveredTo || "";
            const toAddr = msg.to?.[0]?.address || "";
            const returnPath = msg.returnPath || "";
            const isFlagged = !!(msg as any).isFlagged;
            const reputation = (msg as any).reputation as {
                flagged: boolean; listedCount: number; checkedCount: number;
                sources: Array<{ service: string; verdict: string }>;
                verdict: string; service: string;
            } | null;
            const reputationFlagged = !!(reputation && reputation.flagged);
            const reputationText = reputationFlagged
                ? `⚠ ${reputation!.listedCount} of ${reputation!.checkedCount} reputation services flag <strong>${escapeText(senderDomain)}</strong> as <strong>${escapeText(reputation!.verdict)}</strong> (${escapeText(reputation!.sources.map(s => s.service).join(", "))})`
                : "";

            const banner = document.createElement("div");
            banner.className = "mv-remote-banner"
                + (isFlagged ? " mv-remote-banner-flagged" : "")
                + (reputationFlagged ? " mv-remote-banner-reputation" : "");
            banner.innerHTML =
                (isFlagged
                    ? `<div class="mv-rb-flagged">⚠ Sender on your watch list (you marked this sender or domain suspicious)</div>`
                    : "") +
                (reputationFlagged
                    ? `<div class="mv-rb-reputation">${reputationText}</div>`
                    : "") +
                `<div class="mv-rb-summary">` +
                    `<span class="mv-rb-toggle">&#x25B8;</span>` +
                    `<span>Remote content blocked</span>` +
                    `<span class="mv-rb-buttons">` +
                        `<button id="btn-load-remote">Load once</button>` +
                        `<button id="btn-allow-sender" title="${escapeText(senderAddr)}">Always: ${escapeText(senderAddr)}</button>` +
                        (senderDomain ? `<button id="btn-allow-domain" title="*@${escapeText(senderDomain)}">Always: *@${escapeText(senderDomain)}</button>` : "") +
                    `</span>` +
                `</div>` +
                `<div class="mv-rb-details" hidden>` +
                    `<div class="mv-rb-info">` +
                        `<div><span class="mv-rb-label">From:</span> ${escapeText(senderName ? `${senderName} <${senderAddr}>` : senderAddr)}</div>` +
                        (deliveredTo ? `<div><span class="mv-rb-label">Delivered-To:</span> ${escapeText(deliveredTo)}</div>` : "") +
                        (toAddr && toAddr !== deliveredTo ? `<div><span class="mv-rb-label">To:</span> ${escapeText(toAddr)}</div>` : "") +
                        (returnPath && returnPath !== senderAddr ? `<div><span class="mv-rb-label">Return-Path:</span> ${escapeText(returnPath)}</div>` : "") +
                    `</div>` +
                    (deliveredTo || toAddr ? `<div class="mv-rb-actions"><button id="btn-allow-to">Always allow to: ${escapeText(deliveredTo || toAddr)}</button></div>` : "") +
                    `<div class="mv-rb-actions">` +
                        `<button id="btn-flag-sender" class="mv-rb-flag-btn" title="${escapeText(senderAddr)}">${isFlagged ? "Unwatch" : "Watch (mark suspicious)"} sender</button>` +
                        (senderDomain ? `<button id="btn-flag-domain" class="mv-rb-flag-btn" title="*@${escapeText(senderDomain)}">Watch domain *@${escapeText(senderDomain)}</button>` : "") +
                    `</div>` +
                    `<div class="mv-rb-actions"><button id="btn-edit-allowlist" title="View / edit the full allowlist">Edit allowlist…</button></div>` +
                `</div>`;
            bodyEl.appendChild(banner);

            // Toggle dropdown — click arrow or text to expand details
            const summary = banner.querySelector(".mv-rb-summary")!;
            const details = banner.querySelector(".mv-rb-details") as HTMLElement;
            const toggle = banner.querySelector(".mv-rb-toggle")!;
            summary.addEventListener("click", (e) => {
                if ((e.target as HTMLElement).tagName === "BUTTON") return;
                details.hidden = !details.hidden;
                toggle.textContent = details.hidden ? "\u25B8" : "\u25BE";
            });

            const loadRemote = async () => {
                banner.remove();
                const full = await getMessage(accountId, uid, true);
                if (full.bodyHtml) {
                    const oldIframe = bodyEl.querySelector("iframe");
                    if (oldIframe) oldIframe.remove();
                    const iframe = document.createElement("iframe");
                    iframe.srcdoc = wrapHtmlBody(full.bodyHtml, true);
                    bodyEl.appendChild(iframe);
                    installPreviewControls(iframe);
                }
                // Critical: refresh both `currentMessage` and the parsedCache
                // with the unblocked version. Otherwise any later showMessage
                // call (folder refresh, sync event, etc.) hits the stale
                // cached BLOCKED message, re-renders the banner, and the
                // user's click looks like it was forgotten — even though the
                // allowlist persist actually worked. Bob 2026-05-09:
                // "you added it to allowed but don't know it!"
                currentMessage = full;
                currentAccountId = accountId;
                parsedCachePut(accountId, folderId, uid, full);
                markSessionAllowed(accountId, uid);
            };

            banner.querySelector("#btn-load-remote")!.addEventListener("click", loadRemote);

            // Always-allow handlers: render the unblocked view IMMEDIATELY,
            // then persist the allowlist entry in the background. Pre-fix
            // these awaited the persist before re-rendering — if the persist
            // threw (GDrive mount unavailable, atomicWrite failure, IPC
            // error), the unhandled rejection silently aborted the handler
            // and the click looked like a no-op. Optimistic order matches
            // the rest of the local-first model: the user's action commits
            // visually, sync surfaces problems via the status bar.
            const persistAllow = async (type: "sender" | "domain" | "recipient", value: string): Promise<void> => {
                try {
                    await allowRemoteContent(type, value);
                } catch (e: any) {
                    const status = document.getElementById("status-sync");
                    if (status) {
                        status.textContent = `Allowlist save failed (${type}: ${value}) — ${e?.message || e}`;
                        status.style.color = "oklch(0.65 0.2 25)";
                    }
                    console.error(`[allowlist] ${type}=${value} save failed:`, e);
                }
            };

            banner.querySelector("#btn-allow-sender")?.addEventListener("click", () => {
                loadRemote();
                persistAllow("sender", senderAddr);
            });

            banner.querySelector("#btn-allow-domain")?.addEventListener("click", () => {
                loadRemote();
                persistAllow("domain", senderDomain);
            });

            banner.querySelector("#btn-allow-to")?.addEventListener("click", () => {
                const addr = deliveredTo || toAddr;
                if (!addr) return;
                loadRemote();
                persistAllow("recipient", addr);
            });

            // Watch (or unwatch) sender / domain — toggles the allowlist's
            // flaggedSenders / flaggedDomains lists (kept under the legacy
            // JSONC field names for backwards compat; UI now uses "watch /
            // suspicious" wording to disambiguate from the per-message ⚑/★
            // marker which means the OPPOSITE — "important to me"). Future
            // messages from this sender/domain show a ⚠ banner at the top
            // of the viewer. Note: marking a single sender is unreliable
            // because spammers rotate addresses — see TODO for rules.jsonc
            // which will support pattern-based matching as a primary tool.
            const onFlagToggle = async (type: "sender" | "domain", value: string) => {
                if (!value) return;
                try {
                    const result = await flagSenderOrDomain(type, value);
                    const status = document.getElementById("status-sync");
                    if (status) status.textContent = result.flagged
                        ? `Watching ${type}: ${value}`
                        : `Unwatched ${type}: ${value}`;
                    // Re-render this message so the banner picks up the new
                    // flagged state without the user having to reselect.
                    // `isRetry=true` bypasses the same-message guard at the
                    // top of showMessage — we genuinely want a fresh paint
                    // here, even though scroll position will reset (the
                    // user just clicked a control, so scroll loss is
                    // expected, not surprising).
                    if (currentMessage) {
                        invalidateParsedCache(currentAccountId, currentMessage.folderId, currentMessage.uid);
                        showMessage(currentAccountId, currentMessage.uid, currentMessage.folderId, specialUse, true).catch(() => { /* */ });
                    }
                } catch (e: any) {
                    const status = document.getElementById("status-sync");
                    if (status) status.textContent = `Watch failed: ${e?.message || e}`;
                }
            };
            banner.querySelector("#btn-flag-sender")?.addEventListener("click", () => onFlagToggle("sender", senderAddr));
            banner.querySelector("#btn-flag-domain")?.addEventListener("click", () => onFlagToggle("domain", senderDomain));

            // "Edit allowlist…" — fires a document-level event that app.ts
            // listens for and opens the JSONC editor pre-selected to
            // allowlist.jsonc. Keeps message-viewer free of the editor import.
            banner.querySelector("#btn-edit-allowlist")?.addEventListener("click", () => {
                document.dispatchEvent(new CustomEvent("mailx-open-jsonc-editor", { detail: { file: "allowlist.jsonc" } }));
            });

        }

        // bodyError / bodyErrorTransient retired in the local-first refactor.
        // Background body fetches that fail emit `bodyFetchError`, surfaced
        // by the module-level listener as a banner — no inline retry.

        // Body in sandboxed iframe
        if (msg.bodyHtml) {
            const iframe = document.createElement("iframe");
            iframe.sandbox.add("allow-same-origin");
            iframe.sandbox.add("allow-popups");
            iframe.sandbox.add("allow-popups-to-escape-sandbox");
            iframe.sandbox.add("allow-top-navigation-by-user-activation");
            // allow-scripts lets OUR injected <script> run (for Android link
            // interception — parent-side contentDocument listeners don't fire
            // reliably on Android WebView). CSP with a nonce restricts script
            // execution to our tag only; inline scripts in the email body are
            // still blocked.
            iframe.sandbox.add("allow-scripts");
            iframe.srcdoc = wrapHtmlBody(msg.bodyHtml, msg.remoteAllowed);
            // Two timing marks: text-painted (DOMContentLoaded, which fires
            // as soon as the HTML is parsed — typically tens of ms) and
            // all-loaded (load, which waits for every <img> to resolve and
            // can be 10+ s on a marketing email with remote content). The
            // user can read the email at the DOMContentLoaded mark; the
            // earlier single `load` mark misled the timing story.
            iframe.addEventListener("load", () => _ptick("iframe load (all resources)"), { once: true });
            const onReady = () => {
                const doc = iframe.contentDocument;
                if (!doc) return;
                const fire = () => _ptick("iframe DOMContentLoaded (text painted)");
                if (doc.readyState === "interactive" || doc.readyState === "complete") fire();
                else doc.addEventListener("DOMContentLoaded", fire, { once: true });
            };
            // srcdoc-driven iframes go through a single `load` lifecycle, but
            // the contentDocument is observable as soon as the iframe is in
            // the DOM. Schedule the readiness check after append.
            bodyEl.appendChild(iframe);
            queueMicrotask(onReady);
            _ptick("iframe srcdoc set");
            installPreviewControls(iframe);
        } else if (msg.bodyText) {
            const pre = document.createElement("pre");
            // Match the HTML-body branch's typography: sans-serif system
            // font, same size + line-height. Default `<pre>` rendering is
            // monospace, which on some platforms substitutes to a serif
            // fallback (Bob 2026-05-12: "why the font change") and looks
            // like a different message from the same conversation.
            // height:100% + overflow:auto make the <pre> its own scroll
            // container. Without them the <pre> grows to its full content
            // height and the parent .mv-body (overflow:hidden) silently
            // clips everything below the fold — no scrollbar, wheel dead
            // (Bob 2026-05-22). The HTML-body branch gets this for free via
            // the iframe; plain-text bodies need it explicitly.
            pre.style.cssText = "padding: 1rem; white-space: pre-wrap; word-break: break-word; "
                + "font-family: system-ui, sans-serif; font-size: 17.5px; line-height: 1.5; "
                + "color: #1a1a2e; background: #fff; margin: 0; height: 100%; overflow: auto;";
            // Auto-linkify URLs in plain text
            pre.innerHTML = linkifyText(msg.bodyText);
            bodyEl.appendChild(pre);
        } else {
            // No bodyHtml AND no bodyText. The daemon thinks the body is
            // on disk (`cached === true`) but extracted nothing. Most
            // common: the body fetch errored earlier (network blip,
            // 401, 5xx), the row was marked cached anyway, and we have
            // an empty .eml. Surface the last known fetch error for
            // this uid if we have one — Bob 2026-05-09: "if there is a
            // fail to fetch then report the problem rather than telling
            // me there is no content."
            const fetchErr = recentFetchErrors.get(`${accountId}:${uid}`);
            if (fetchErr) {
                bodyEl.innerHTML = `<div class="mv-system-message mv-system-error">
                    <div class="mv-system-tag">mailx</div>
                    <div class="mv-system-title">Body fetch failed</div>
                    <div class="mv-system-body">${escapeHtml(fetchErr.error)}<br><span style="color:var(--color-text-muted);font-size:0.9em">Recorded ${Math.round((Date.now() - fetchErr.when) / 1000)}s ago. ${fetchErr.transient ? "Will retry automatically." : "Permanent — server-side delete may have raced."}</span></div>
                </div>`;
            } else {
                // Empty parse result. The COMMON case is benign — a
                // subject-only note (Bob 2026-05-22: "Remember Fish on
                // Monday" sent with no body), or an attachment-only message.
                // Lead with a calm one-liner, not the alarming parser dump.
                // The traceability crumbs (version, path, uid) stay, but
                // muted and compact — enough to debug a genuine stub without
                // making a normal empty message look broken.
                const emlPath = (msg as any).emlPath || "";
                const attCount = msg.attachments?.length || 0;
                const appVer = (document.querySelector(".app-version, #app-version, #status-version")?.textContent || "").trim();
                const headline = attCount > 0
                    ? "This message has attachments but no body text."
                    : "This message has no body text — only a subject.";
                const crumbs = `mailx${appVer ? " " + appVer : ""} · ${accountId}/${uid}${emlPath ? " · " + emlPath : ""}`;
                bodyEl.innerHTML = `<div class="mv-system-message">
                    <div class="mv-system-body">${escapeHtml(headline)}</div>
                    <div class="mv-system-body" style="color:var(--color-text-muted);font-size:0.8em;margin-top:8px">${escapeHtml(crumbs)}</div>
                </div>`;
            }
        }

        // Attachments — always clear first to avoid stale chips from previous message
        attEl.innerHTML = "";
        attEl.hidden = true;
        if (msg.attachments?.length) {
            attEl.hidden = false;
            // ── Drag-out prefetch cache ──
            // dataTransfer.setData ONLY works synchronously inside dragstart —
            // the drag data store closes at the first `await` — so the old
            // handler that fetched the attachment inside dragstart silently
            // set nothing, and drag-to-Explorer never worked (Bob 2026-07-02).
            // Prefetch every attachment's bytes in the background right after
            // the chips render; dragstart reads this cache synchronously.
            // Blob URLs from the previous message are revoked first.
            for (const u of dragOutUrls) { try { URL.revokeObjectURL(u); } catch { /* */ } }
            dragOutUrls.length = 0;
            const dragCache = new Map<number, { url: string; contentType: string }>();
            void (async () => {
                for (const a1 of msg.attachments) {
                    if ((a1.size || 0) > 50 * 1024 * 1024) continue; // don't buffer >50MB for a maybe-drag
                    try {
                        const data = await getAttachment(accountId, uid, a1.id, msg.folderId);
                        const bytes = Uint8Array.from(atob(data.content), c => c.charCodeAt(0));
                        const blob = new Blob([bytes], { type: data.contentType || "application/octet-stream" });
                        const url = URL.createObjectURL(blob);
                        dragOutUrls.push(url);
                        dragCache.set(a1.id, { url, contentType: data.contentType || "application/octet-stream" });
                    } catch { /* drag-out just won't arm for this attachment */ }
                }
            })();
            // Save (download to disk) one attachment by index — distinct from
            // the click handler's "open with the OS app". Desktop (WebView2 =
            // Chromium) gets a real Save-As dialog via showSaveFilePicker;
            // Android / --server fall back to blob + <a download> → Downloads
            // (the only save mechanism that works in every host).
            const saveAttachmentAt = async (idx: number): Promise<void> => {
                const a0 = msg.attachments[idx];
                const name = a0.filename || "attachment";
                try {
                    const data = await getAttachment(accountId, uid, a0.id, msg.folderId);
                    const bytes = Uint8Array.from(atob(data.content), c => c.charCodeAt(0));
                    const blob = new Blob([bytes], { type: data.contentType || "application/octet-stream" });
                    const picker = (window as any).showSaveFilePicker;
                    if (typeof picker === "function") {
                        // Native Save-As. A user cancel rejects with AbortError —
                        // that's not an error, so swallow it silently.
                        let handle: any;
                        try {
                            handle = await picker({ suggestedName: name });
                        } catch (e: any) {
                            if (e?.name === "AbortError") return;
                            throw e;
                        }
                        const writable = await handle.createWritable();
                        await writable.write(blob);
                        await writable.close();
                        return;
                    }
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url; a.download = name; a.style.display = "none";
                    document.body.appendChild(a); a.click();
                    setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 5000);
                } catch (err: any) {
                    window.dispatchEvent(new CustomEvent("mailx-alert", { detail: { message: `Couldn't save "${name}": ${err?.message || err}`, key: "attachment-save" } }));
                }
            };
            const saveAllAttachments = async (): Promise<void> => {
                // Stagger so the host registers each download as a separate file
                // instead of collapsing rapid-fire blob clicks.
                for (let k = 0; k < msg.attachments.length; k++) {
                    await saveAttachmentAt(k);
                    await new Promise(r => setTimeout(r, 300));
                }
            };
            for (let i = 0; i < msg.attachments.length; i++) {
                const att = msg.attachments[i];
                // Button (not <a href="#">): the browser/WebView2 native
                // context menu offered "Open in new window" which navigated
                // to msger.localhost/index.html# and 404'd. Switching to a
                // button removes that path; download happens via a
                // programmatic <a download> click which is reliable across
                // msger / WebView2 / Android WebView (window.open of a blob
                // URL is silently blocked in some hosts).
                const chip = document.createElement("button");
                chip.type = "button";
                chip.className = "mv-att-chip";
                chip.textContent = `\uD83D\uDCCE ${att.filename} (${formatSize(att.size)})`;
                chip.title = `${att.filename} (${att.mimeType})`;
                chip.addEventListener("click", async (e) => {
                    e.preventDefault();
                    // Click feedback + re-click guard. Opening hands off to the
                    // OS viewer (~200ms) with no visible change in mailx, so the
                    // user used to click 3-4 times. Show "Opening…" for at least
                    // 600ms and ignore clicks while it's in flight.
                    if (chip.dataset.opening === "1") return;
                    chip.dataset.opening = "1";
                    const chipLabel = chip.textContent || "";
                    chip.textContent = "⏳ Opening…";
                    const tStart = Date.now();
                    const restoreChip = (): void => {
                        const wait = Math.max(0, 600 - (Date.now() - tStart));
                        setTimeout(() => { chip.textContent = chipLabel; chip.dataset.opening = ""; }, wait);
                    };
                    try {
                        const bridge = (window as any)._nativeBridge;
                        if (bridge?.openAttachment) {
                            // Android: blob URLs don't work in WebView. Pass base64
                            // to native bridge which saves to Downloads and opens
                            // with the system viewer.
                            const data = await getAttachment(accountId, uid, att.id, msg.folderId);
                            await bridge.openAttachment(att.filename, data.contentType, data.content);
                            return;
                        }
                        // Desktop (IPC): the Node service saves the file and opens
                        // it with the OS default app. openAttachment() returns the
                        // result object when an IPC host handled it, or `undefined`
                        // only when the host has no such method (real browser /
                        // --server mode). A genuine service failure throws \u2014 we do
                        // NOT blob-fall-back in that case (the service is the only
                        // thing that can open a file on the desktop, and the blob
                        // path just produced a misleading second error).
                        const res = await openAttachment(accountId, uid, att.id, msg.folderId, att.filename);
                        if (res) return;
                        // res === undefined \u2192 real browser / --server mode only.
                        const data = await getAttachment(accountId, uid, att.id, msg.folderId);
                        const bytes = Uint8Array.from(atob(data.content), c => c.charCodeAt(0));
                        const blob = new Blob([bytes], { type: data.contentType });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = att.filename || "attachment";
                        a.style.display = "none";
                        document.body.appendChild(a);
                        a.click();
                        // Defer revoke until the download has had time to start.
                        setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 5000);
                    } catch (err: any) {
                        // Non-blocking banner \u2014 never alert(): a modal alert
                        // freezes the WebView event loop, which stalls IPC event
                        // delivery and makes sync look like it stopped.
                        const m = `Couldn't open "${att.filename}": ${err?.message || err}`;
                        console.error(m);
                        window.dispatchEvent(new CustomEvent("mailx-alert", { detail: { message: m, key: "attachment-open" } }));
                    } finally {
                        restoreChip();
                    }
                });
                // Drag the chip to an external target (Explorer / Finder) to
                // drop the file there. Chromium `DownloadURL` dataTransfer
                // format: "mime:filename:blob-url". MUST be synchronous — the
                // content comes from the prefetch cache above; Chromium stamps
                // the dropped file with the Mark-of-the-Web like any download.
                chip.draggable = true;
                chip.addEventListener("dragstart", (e) => {
                    if (!e.dataTransfer) return;
                    const cached = dragCache.get(att.id);
                    if (!cached) {
                        // Prefetch still in flight (or >50MB skip). Cancel the
                        // drag rather than start one that can't carry data.
                        e.preventDefault();
                        const statusEl = document.getElementById("status-sync");
                        if (statusEl) statusEl.textContent = "Attachment still loading — try dragging again in a moment.";
                        return;
                    }
                    // Sanitize filename: no path separators, no newlines.
                    const safeName = (att.filename || "attachment").replace(/[\r\n"\/\\]/g, "_");
                    e.dataTransfer.setData("DownloadURL", `${cached.contentType}:${safeName}:${cached.url}`);
                    e.dataTransfer.effectAllowed = "copy";
                });
                // Right-click → Open / Save / Save all. Click still opens; this
                // adds explicit save (download to disk) which the click path
                // didn't offer (Bob 2026-06-04).
                chip.addEventListener("contextmenu", (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const items: { label: string; action: () => void; separator?: boolean }[] = [
                        { label: "Open", action: () => chip.click() },
                        { label: `Save "${att.filename}"…`, action: () => { void saveAttachmentAt(i); } },
                    ];
                    if (msg.attachments.length > 1) {
                        items.push({ label: `Save all (${msg.attachments.length})…`, action: () => { void saveAllAttachments(); } });
                    }
                    showContextMenu(e.clientX, e.clientY, items);
                });
                attEl.appendChild(chip);
            }
        }
    } catch (e: any) {
        const err = e.message || "Unknown error";
        console.error("showMessage error:", e);
        // A body-fetch failure (corrupt/unrelated .eml, parse throw) must NOT
        // leave the header hidden. Line 528 hides it on the no-cache path; if
        // we then throw, the user got an error body with no From/Subject/Date
        // and no Reply/Source/Delete toolbar — "have a post but can't see the
        // header portion" (Bob 2026-06-05). Render the header from whatever
        // envelope we have so the user always sees WHICH message failed and
        // can still act on it. renderHeaderFromEnvelope unhides + is defensive.
        const failEnv = cached || lastEnvelope || envelope;
        if (failEnv) { try { renderHeaderFromEnvelope(headerEl, failEnv); } catch { /* */ } }
        // "Message was deleted from the server" — the service already dropped
        // the local row. Remove it from the list so the UI advances to the next
        // message instead of sitting on a stale error banner.
        const isNotFound = /deleted from the server|isNotFound|not found|Not Found|404/.test(err);
        if (isNotFound) {
            // Drop the stale row so the list auto-advances to the next message
            // (or clears the viewer). Leaves the user a way back on mobile where
            // the viewer takes the whole screen. The list owns focus handoff —
            // we surface the removal as an event and let it run its own
            // removeMessages flow (which both filters the list and re-focuses
            // a survivor or clears this pane).
            document.dispatchEvent(new CustomEvent("mailx-remove-stale", {
                detail: { accountId, uid },
            }));
            return;
        }
        if (retryCount < 3) {
            retryCount++;
            bodyEl.innerHTML = `<div class="mv-empty">Loading failed: ${err} — retrying (${retryCount}/3)...</div>`;
            setTimeout(() => { if (gen === showMessageGeneration) showMessage(accountId, uid, folderId, specialUse, true); }, 3000);
        } else {
            bodyEl.innerHTML = `<div class="mv-empty">Failed to load: ${err}</div>`;
        }
    }
}

function formatAddr(addr: { name: string; address: string }): string {
    if (addr.name) return `${addr.name} <${addr.address}>`;
    return addr.address;
}

/** Render the viewer header from a list-row envelope (instant — no body
 *  fetch awaited). Used to populate the header pane the moment a message
 *  is clicked so the user always sees something actionable; getMessage()
 *  later overwrites the same fields with the authoritative values from the
 *  body parse (which can add Cc, Delivered-To, etc. that the list row
 *  doesn't track). */
function renderHeaderFromEnvelope(headerEl: HTMLElement, env: any): void {
    headerEl.hidden = false;
    const fromEl = headerEl.querySelector(".mv-from");
    const toEl = headerEl.querySelector(".mv-to");
    const subjEl = headerEl.querySelector(".mv-subject");
    const dateEl = headerEl.querySelector(".mv-date");
    if (fromEl) fromEl.textContent = formatAddr(env.from);
    if (toEl) {
        let toLine = `To: ${(env.to || []).map(formatAddr).join(", ")}`;
        if (env.cc?.length) toLine += `  Cc: ${env.cc.map(formatAddr).join(", ")}`;
        toEl.textContent = toLine;
    }
    if (subjEl) subjEl.textContent = env.subject || "";
    if (dateEl) {
        try { dateEl.textContent = new Date(env.date).toLocaleString(); }
        catch { dateEl.textContent = ""; }
    }
}

function escapeHtml(s: string): string {
    return (s || "").replace(/[&<>"']/g, c =>
        ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" }[c]!));
}

/** Convert plain text URLs into clickable links, escaping HTML */
function linkifyText(text: string): string {
    // Escape HTML first
    const escaped = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    // Then linkify URLs. The character class already stops the match at
    // whitespace and <>"')] but NOT at sentence punctuation, so a URL that
    // ends a clause — "…25965.pdf, in case…" — would otherwise pull the
    // trailing comma into the link (Bob 2026-06-11). Peel a trailing run of
    // sentence punctuation off the match and emit it as plain text after the
    // anchor. The run must be pure punctuation, so an internal dot like the
    // ".pdf" extension is untouched (only the final ",", ".", etc. is shed).
    return escaped.replace(
        /(https?:\/\/[^\s<>"')\]]+)/g,
        (match: string) => {
            const m = match.match(/^(.*?)([.,;:!?]+)$/);
            const url = m ? m[1] : match;
            const trailer = m ? m[2] : "";
            return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>${trailer}`;
        }
    );
}

function escapeText(s: string): string {
    const div = document.createElement("div");
    div.textContent = s;
    return div.innerHTML;
}

/** Minimal add-contact modal: name + email + organization with a duplicate
 *  check (checks the contacts DB for an existing row with the same email
 *  and surfaces it so the user can update instead of creating a second
 *  row with a different name). Future: AI-extracted fields from the letter
 *  body populate the form before it opens. */
async function showAddContactDialog(nameIn: string, emailIn: string): Promise<void> {
    let dup: { name: string; email: string; source: string } | null = null;
    try {
        const existing = await listContacts(emailIn, 1, 10);
        const match = (existing?.items || []).find((c: any) => (c.email || "").toLowerCase() === emailIn.toLowerCase());
        if (match) dup = match;
    } catch { /* non-fatal — dialog still works without dup info */ }

    // Source-aware status line. "discovered" = local cache auto-collected from
    // mail, NOT in Google Contacts — the old "Already in address book" wording
    // conflated the two and confused Bob (2026-06-04). Make the distinction
    // explicit, and say what Save actually does (upsertContact pushes to Google).
    let dupInfo = "";
    if (dup) {
        const nm = `<strong>${escapeText(dup.name || "(no name)")}</strong>`;
        if (dup.source === "discovered") {
            dupInfo = `<div class="mailx-modal-info">Found in <em>local discovered contacts</em> (auto-collected from your mail) as ${nm} — <strong>not yet in Google Contacts</strong>. Saving adds it to Google Contacts.</div>`;
        } else if (dup.source === "google") {
            dupInfo = `<div class="mailx-modal-info">Already in <strong>Google Contacts</strong> as ${nm}. Saving updates the name.</div>`;
        } else {
            dupInfo = `<div class="mailx-modal-info">Already saved as ${nm} (${escapeText(dup.source)}). Saving updates the name and syncs to Google Contacts.</div>`;
        }
    }
    // Button label: a discovered (local-only) contact is being PROMOTED to
    // Google, so "Add to Google Contacts" is truthful; an existing Google
    // contact is an "Update".
    const saveLabel = dup ? (dup.source === "discovered" ? "Add to Google Contacts" : "Update") : "Save";
    const backdrop = document.createElement("div");
    backdrop.className = "mailx-modal-backdrop";
    const panel = document.createElement("div");
    panel.className = "mailx-modal";
    panel.innerHTML = `
        <div class="mailx-modal-title">
            <span class="mailx-modal-title-text">${dup ? "Update contact" : "Add contact"}</span>
            <button type="button" class="mailx-modal-close" id="ac-close" aria-label="Close">&times;</button>
        </div>
        ${dupInfo}
        <label class="mailx-modal-label">Name
            <input class="mailx-modal-input" id="ac-name" type="text" value="${escapeText(dup?.name || nameIn || "")}" autofocus>
        </label>
        <label class="mailx-modal-label">Email
            <input class="mailx-modal-input" id="ac-email" type="email" value="${escapeText(emailIn)}" readonly>
        </label>
        <label class="mailx-modal-label">Organization <span style="color:var(--color-text-muted);font-size:0.85em">(optional)</span>
            <input class="mailx-modal-input" id="ac-org" type="text" placeholder="">
        </label>
        <div class="mailx-modal-buttons">
            <span class="mailx-modal-spacer"></span>
            <button type="button" class="mailx-modal-btn" data-action="cancel">Cancel</button>
            <button type="button" class="mailx-modal-btn mailx-modal-btn-primary" data-action="save">${escapeText(saveLabel)}</button>
        </div>`;
    backdrop.appendChild(panel);
    document.body.appendChild(backdrop);

    const close = () => backdrop.remove();
    panel.querySelector<HTMLButtonElement>("#ac-close")!.addEventListener("click", close);
    panel.querySelectorAll<HTMLButtonElement>(".mailx-modal-btn").forEach(btn => {
        btn.addEventListener("click", async () => {
            if (btn.dataset.action === "cancel") { close(); return; }
            const nameEl = panel.querySelector<HTMLInputElement>("#ac-name")!;
            const emailEl = panel.querySelector<HTMLInputElement>("#ac-email")!;
            btn.disabled = true;
            btn.textContent = "Saving…";
            try {
                // upsertContact is the two-way cache path (enqueues a Google
                // People push); for pure local-first addContact would also
                // work but skips the Google sync. Use upsertContact so the
                // row propagates to Google Contacts next drain tick.
                await upsertContact(nameEl.value.trim(), emailEl.value.trim());
                close();
            } catch (e: any) {
                btn.disabled = false;
                btn.textContent = saveLabel;
                alert(`Couldn't save: ${e?.message || e}`);
            }
        });
    });
    const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") { close(); document.removeEventListener("keydown", onKey, true); }
    };
    document.addEventListener("keydown", onKey, true);
    // addContact is kept as a legacy silent path (no-form) for any caller
    // that still invokes it — currently none after this refactor.
    void addContact;
}

function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
}

export function wrapHtmlBody(html: string, allowRemote = false): string {
    // CSP blocks remote resources (tracking pixels, external CSS). Inline
    // scripts are allowed via 'unsafe-inline' so our injected link-tap handler
    // runs; email-body <script> tags and on* handlers are stripped server-side
    // by sanitizeHtml() in mailx-types, so this doesn't actually widen the
    // attack surface. (A per-render nonce would be tidier, but meta-CSP with
    // nonces isn't reliably honored across older WebViews — and when a nonce
    // is present, 'unsafe-inline' is ignored, so our script fell back to
    // blocked on those WebViews.)
    const csp = allowRemote
        ? ""
        : `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src data: cid:; form-action 'none';">`;
    return `<!DOCTYPE html>
<html><head>
<meta charset="UTF-8">
${csp}
<style>
    html, body { touch-action: pan-y pinch-zoom; }
    html { height: 100%; overflow-y: auto; overflow-x: hidden; }
    body {
        font-family: system-ui, sans-serif;
        font-size: 17.5px;
        line-height: 1.5;
        color: #1a1a2e;
        background: #fff;
        padding: 1rem;
        margin: 0;
        min-height: 100%;
        word-break: break-word;
        color-scheme: dark light;
    }
    /* max-width keeps wide images inside the pane; modern WebView2 preserves
       aspect ratio from the width/height attributes automatically when it
       shrinks them, so we must NOT force height:auto. Forcing it overrode the
       explicit pixel height on sliced layout images (e.g. a 12x226 rounded-
       corner slice) — when an email's responsive @media rule widened the
       slice's cell, height:auto recomputed height from the tall aspect ratio
       and ballooned the slice to full-page height, pushing all text far below
       the fold (Bob 2026-06-10: Chase fraud-alert "scroll way down to see text"). */
    img { max-width: 100%; }
    a { color: #1a6dd4; }
    pre, code { white-space: pre-wrap; }
    blockquote { border-left: 3px solid #ccc; padding-left: 1rem; margin-left: 0; color: #666; }
    @media (prefers-color-scheme: dark) {
        body { color: #cdd6f4; background: #282840; }
        a { color: #89b4fa; }
        blockquote { border-color: #45475a; color: #6c7086; }
    }
</style>
<base target="_blank">
<script>
// Link interception — Android WebView doesn't fire the default <a target="_blank">
// new-window handler, so we postMessage to the parent which routes through the
// native bridge (mailxapi://openurl) to Launcher.OpenAsync.
(function () {
    function handleLinkTap(e) {
        var a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
        if (!a) return;
        var url = a.href;
        if (!url || url.indexOf("javascript:") === 0 || url.charAt(0) === "#") return;
        e.preventDefault();
        e.stopPropagation();
        try { console.log("[android-link] preview tap intercepted → " + url); } catch (e2) { /* */ }
        window.parent.postMessage({ type: "linkClick", url: url }, "*");
    }
    document.addEventListener("click", handleLinkTap, true);
    // Android WebView fallback: some builds drop the synthetic click after
    // touchend, so treat a stationary touchstart→touchend on the same link
    // as a tap. Anything that moves more than TAP_SLOP pixels is a scroll
    // and must NOT activate the link.
    var TAP_SLOP = 10;
    var lastTouchTarget = null;
    var lastTouchX = 0;
    var lastTouchY = 0;
    var touchMoved = false;
    // All touch listeners are passive so Android WebView can compositor-scroll
    // the iframe without waiting on our JS. handleLinkTap's preventDefault only
    // matters for the "click" path (which is non-passive by default).
    document.addEventListener("touchstart", function (e) {
        var t0 = e.touches && e.touches[0];
        lastTouchX = t0 ? t0.clientX : 0;
        lastTouchY = t0 ? t0.clientY : 0;
        touchMoved = false;
        lastTouchTarget = (e.target && e.target.closest) ? e.target.closest("a[href]") || e.target : e.target;
    }, { passive: true, capture: true });
    document.addEventListener("touchmove", function (e) {
        if (touchMoved) return;
        var t = e.touches && e.touches[0];
        if (!t) return;
        if (Math.abs(t.clientX - lastTouchX) > TAP_SLOP || Math.abs(t.clientY - lastTouchY) > TAP_SLOP) {
            touchMoved = true;
            lastTouchTarget = null;
        }
    }, { passive: true, capture: true });
    document.addEventListener("touchend", function (e) {
        if (touchMoved) { lastTouchTarget = null; touchMoved = false; return; }
        var t = (e.target && e.target.closest) ? e.target.closest("a[href]") || e.target : e.target;
        if (lastTouchTarget && lastTouchTarget === t) handleLinkTap(e);
        lastTouchTarget = null;
    }, { passive: true, capture: true });
    document.addEventListener("touchcancel", function () {
        lastTouchTarget = null; touchMoved = false;
    }, { passive: true, capture: true });
    // Link hover preview — posts the link URL to the parent on hover so
    // the parent can show a tooltip after a 500 ms dwell. Earlier removal
    // (2026-04-24) was because dismissers in the parent (mousedown / scroll
    // / blur) don't fire while the mouse is inside the iframe, leaving the
    // popover stuck. Restored 2026-05-09 with the proper fix: the iframe
    // explicitly posts an empty-URL message on link mouseout AND on body
    // mousemove-away, so the parent reliably hides regardless of which
    // dismissers it sees. The parent's debounced 500 ms show-timer still
    // suppresses flicker.
    var lastHoveredHref = "";
    function postLinkHover(href, rect, mouse) {
        // Only post on transitions to avoid spamming the parent on every
        // mousemove inside a link.
        if (href === lastHoveredHref) return;
        lastHoveredHref = href;
        window.parent.postMessage({
            type: "linkHover",
            url: href,
            rect: rect ? { left: rect.left, top: rect.top, right: rect.right, bottom: rect.bottom } : null,
            // Cursor position (iframe-viewport coords) so the parent can anchor
            // the tooltip AT the pointer, not at the link's bottom-left corner —
            // on a wide link/banner the corner is far from the cursor (Bob
            // 2026-06-13: "displays the URL far from the cursor").
            mouse: mouse || null,
        }, "*");
    }
    document.addEventListener("mouseover", function (e) {
        var a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
        if (!a) return;
        var href = a.getAttribute("href") || "";
        if (!href || href.charAt(0) === "#" || href.indexOf("javascript:") === 0) return;
        var r = a.getBoundingClientRect();
        postLinkHover(a.href || href, r, { x: e.clientX, y: e.clientY });
    }, true);
    document.addEventListener("mouseout", function (e) {
        var a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
        if (!a) return;
        // Only fire when leaving the link entirely (not into a child element).
        var to = e.relatedTarget;
        if (to && to.closest && to.closest("a[href]") === a) return;
        postLinkHover("", null);
    }, true);
    // Mouseleave on body covers the case where the cursor exits the
    // iframe entirely without firing mouseout on the link (Edge / older
    // WebView2 builds). Belt-and-suspenders dismissal.
    document.addEventListener("mouseleave", function () {
        if (lastHoveredHref) postLinkHover("", null);
    }, true);
    // Touch dismissers — a tap on Android fires synthetic mouseover but
    // often skips the matching mouseout (no mouse-move to track), so the
    // URL preview can stick on top of the message body even after the
    // user has scrolled away (Bob 2026-05-26 "sticky overlay URL"). Any
    // touchstart / touchend / scroll anywhere in the iframe immediately
    // clears the tooltip.
    var clearHover = function () { if (lastHoveredHref) postLinkHover("", null); };
    document.addEventListener("touchstart", clearHover, { passive: true, capture: true });
    document.addEventListener("touchend",   clearHover, { passive: true, capture: true });
    document.addEventListener("scroll",     clearHover, { passive: true, capture: true });
    window.addEventListener("scroll",       clearHover, { passive: true, capture: true });
    // Note: iframe-level dblclick fullscreen toggle REMOVED — it hijacked
    // word-selection (Bob 2026-05-11). Double-clicking text inside the
    // preview now selects the word like any other browser surface; the
    // fullscreen toggle lives on the viewer chrome (mv-header) handler
    // in app.ts, which explicitly excludes interactive controls but
    // operates on chrome, not iframe content.

    // Receive commands from the parent (Copy / Select all menu actions).
    // Browsers restrict execCommand("copy") to the document where focus
    // lives — running it in the parent against the iframe's selection is
    // a no-op, so the parent posts the command in and we run it here.
    window.addEventListener("message", function (e) {
        var d = e.data;
        if (!d || d.type !== "previewCommand") return;
        if (d.cmd === "copy") {
            try { document.execCommand("copy"); } catch (_) {}
        } else if (d.cmd === "selectAll") {
            try {
                var s = window.getSelection && window.getSelection();
                if (s) {
                    var r = document.createRange();
                    r.selectNodeContents(document.body);
                    s.removeAllRanges();
                    s.addRange(r);
                }
            } catch (_) {}
        }
    });

    // Right-click → unified context menu. Always send 'previewContextMenu'
    // with both link info (when over a link) AND selection / body access.
    // Quora-style pages are wall-to-wall links — branching on target meant
    // body actions (Translate / Zoom) were unreachable. Combined menu
    // shows link actions first when applicable, body actions always.
    // Suppresses the native WebView2 menu unconditionally — without that,
    // its Back/Refresh/Save-as/Inspect menu fires before the parent
    // contentDocument listener attached.
    document.addEventListener("contextmenu", function (e) {
        e.preventDefault();
        e.stopPropagation();
        var rect = { left: 0, top: 0 };
        try { rect = e.target.getBoundingClientRect ? e.target.getBoundingClientRect() : rect; } catch (_) {}
        var a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
        var img = e.target && e.target.closest ? e.target.closest("img") : null;
        var sel = (window.getSelection && window.getSelection()) ? window.getSelection().toString() : "";
        window.parent.postMessage({
            type: "previewContextMenu",
            selectedText: sel,
            linkUrl: a ? a.href : "",
            linkText: a ? (a.textContent || "").slice(0, 100) : "",
            imgSrc: img ? (img.currentSrc || img.src || "") : "",
            x: e.clientX, y: e.clientY,
            iframeLeft: rect.left, iframeTop: rect.top
        }, "*");
    });
    // Iframe pointerdown → tell the parent to dismiss any open context menu.
    // The parent's dismissListener listens to its own document's pointerdown,
    // but iframe events don't bubble across the boundary; without this, a
    // click in the message body to dismiss the menu would be invisible to
    // the parent and the menu would stick open.
    document.addEventListener("pointerdown", function (e) {
        // The contextmenu handler above ALSO fires pointerdown on right-click;
        // that one opens a fresh menu, which already closes the previous.
        // Skip right-click (button 2) to avoid a publish/close race.
        if (e.button === 2) return;
        try { window.parent.postMessage({ type: "iframePointerDown" }, "*"); } catch (_) {}
    }, true);
    // Key forwarding — Delete, Ctrl+D, arrow keys, etc. need to reach app.ts
    // even when focus is inside the sandboxed iframe. Parent-side
    // contentDocument listeners (see installPreviewControls) work on
    // desktop WebView2 but not Android WebView, so we post every keydown
    // that isn't plain typing.
    document.addEventListener("keydown", function (e) {
        var t = e.target;
        if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
        // Zoom keys handled by parent-side installPreviewControls; don't double-send.
        if (e.ctrlKey && (e.key === "=" || e.key === "+" || e.key === "-" || e.key === "0")) return;
        // Ctrl+A stays in the preview: native select-all on THIS document so
        // the letter text can be copied. Don't suppress, don't forward — the
        // list's select-all-rows must not fire while focus is in the preview
        // (Bob 2026-07-18). Mirrors the parent-side handler's early return.
        var klc = (e.key || "").toLowerCase();
        if (e.ctrlKey && !e.altKey && !e.metaKey && klc === "a") return;
        // Preventing the iframe's default for keys we forward to the parent
        // is essential — the parent's preventDefault on the synthetic
        // keydown can't suppress the browser's reaction to the ORIGINAL
        // event (Ctrl+R reload, Ctrl+F find, etc.). Suppress here so the
        // browser doesn't act before the parent processes the action.
        var k = klc;
        var isShortcut = e.ctrlKey && !e.altKey && !e.metaKey && (
            k === "r" || k === "f" || k === "n" || k === "d" ||
            k === "z" || k === "y" || k === "k" ||
            // Ctrl+P: without this suppress, WebView2's native print fires on
            // the iframe's default and prints the WHOLE APP window instead of
            // the letter (Bob 2026-07-02). The forwarded event reaches app.ts,
            // which runs printCurrentMessage (letter + header block).
            k === "p"
        );
        if (isShortcut) e.preventDefault();
        window.parent.postMessage({
            type: "previewKey",
            key: e.key, code: e.code,
            ctrlKey: e.ctrlKey, shiftKey: e.shiftKey, altKey: e.altKey, metaKey: e.metaKey,
        }, "*");
    });
})();
</script>
</head><body>${html}</body></html>`;
}

/** Open the current message in a separate view: floating draggable overlay
 *  on desktop (multiple at once, like compose), full-screen mode on mobile.
 *  Threshold matches the layout.css responsive breakpoint so the experience
 *  is consistent with other narrow-mode behavior. Snapshot in time — the
 *  pop-out doesn't auto-update if the user clicks another message. */
export function popOutCurrentMessage(): void {
    if (!currentMessage) return;
    // Use the fullscreen toggle on any touch-primary device, not just <=768
    // px wide. Android tablets / large phones in landscape can be wider
    // than 768 but the desktop "spawn a floating draggable overlay" path
    // doesn't work well there — touch can't drag the title bar, and on
    // some WebView2 builds the overlay paints off-screen or under the
    // taskbar. Treat coarse-pointer hosts (which is what Android always
    // is) as the fullscreen case (Bob 2026-05-26 "the box with arrow
    // doesn't pop up nor go full window").
    const isAndroid = (window as any).mailxapi?.platform === "android";
    // Use the in-window fullscreen toggle ONLY for genuinely touch-only or
    // narrow hosts. A touch-CAPABLE desktop (e.g. a Windows laptop with a
    // touchscreen) matches `(pointer: coarse)` even when the user is on the
    // mouse — the old check sent it to fullscreen, which has no Reply/Forward/
    // Delete toolbar, so the popout "didn't offer Reply" (Bob 2026-06-29; same
    // misdetection class as the multi-select bug). Prefer the desktop popout
    // (with its action toolbar + working links) whenever a real pointer exists.
    const hasMouse = !!window.matchMedia?.("(any-pointer: fine)")?.matches;
    const isNarrow = window.innerWidth <= 768;
    if (isAndroid || isNarrow || !hasMouse) {
        document.body.classList.toggle("viewer-fullscreen");
        return;
    }
    // Drafts: popout means "open in compose for editing", not "open as
    // read-only window". The read-only popout has no Reply / Edit / Send
    // buttons, so handing a draft to it is a dead end. Same routing as
    // the double-click → mailx-popout-message handler in app.ts.
    const flags: string[] = Array.isArray((currentMessage as any).flags) ? (currentMessage as any).flags : [];
    if (flags.includes("\\Draft")) {
        document.dispatchEvent(new CustomEvent("mailx-popout-message", {
            detail: {
                accountId: currentAccountId,
                uid: (currentMessage as any).uid,
                folderId: (currentMessage as any).folderId,
                subject: (currentMessage as any).subject,
            },
        }));
        return;
    }
    spawnDesktopPopout(currentMessage, currentAccountId);
}

/** Pop the message COMPLETELY out into its own OS window (not the in-window
 *  overlay). The daemon spawns a NATIVE msger WebView window pointed at its
 *  loopback popout server (Bob 2026-07-06: "the 🗗 window button should use
 *  msger not the browser") — window.open is gone from this path; a browser
 *  option can come later by pointing the OS browser at the same URL. The
 *  popout page's toolbar routes Reply/Forward/etc back to the MAIN window,
 *  where the real editable compose opens. Falls back to the in-window overlay
 *  when there's no popout host (server didn't bind, non-IPC host). */
export function popOutToWindow(): void {
    if (!currentMessage) return;
    const a = currentAccountId;
    const u = (currentMessage as any).uid;
    const f = (currentMessage as any).folderId;
    const subject = (currentMessage as any).subject || "";
    void (async () => {
        try {
            const { popoutWindow } = await import("../lib/api-client.js");
            const r = await popoutWindow(a, u, f, subject);
            if (!r?.ok) {
                console.log(`[popout-window] daemon declined (${r?.reason || "no reason"}) → overlay fallback`);
                popOutCurrentMessage();
            }
        } catch (e: any) {
            console.log(`[popout-window] popoutWindow failed: ${e?.message || e} → overlay fallback`);
            popOutCurrentMessage();
        }
    })();
}

/** Print the currently-displayed message. Builds a self-contained printable
 *  document — a header block (From/To/Cc/Subject/Date) plus the message body
 *  — renders it in an off-screen iframe, and fires the print dialog. There
 *  was no way to print a letter before (Bob 2026-05-21). */
/** Full standalone HTML document for a message: printed-letter header block
 *  (From/To/Cc/Subject/Date) + body. Shared by Print and Save-as-HTML so the
 *  saved file reads exactly like the printout. */
function buildPrintableDoc(m: any): string | null {
    if (!m) return null;
    const fmt = (a: { name?: string; address?: string }): string =>
        a?.name ? `${a.name} <${a.address || ""}>` : (a?.address || "");
    const esc = escapeHtmlLocal;
    const row = (label: string, value: string): string =>
        value ? `<div><strong>${label}:</strong> ${esc(value)}</div>` : "";
    const headerHtml =
        `<div style="font-family:system-ui,Segoe UI,sans-serif;font-size:11pt;` +
        `border-bottom:1px solid #999;padding-bottom:8px;margin-bottom:14px;line-height:1.5;">` +
        row("From", fmt(m.from || {})) +
        row("To", (m.to || []).map(fmt).join(", ")) +
        (m.cc?.length ? row("Cc", m.cc.map(fmt).join(", ")) : "") +
        row("Subject", m.subject || "") +
        row("Date", m.date ? new Date(m.date).toLocaleString() : "") +
        `</div>`;
    const bodyHtml = m.bodyHtml
        ? m.bodyHtml
        : `<pre style="white-space:pre-wrap;word-break:break-word;font-family:system-ui,sans-serif;">${esc(m.bodyText || "")}</pre>`;
    return `<!DOCTYPE html><html><head><meta charset="utf-8">` +
        `<title>${esc(m.subject || "Message")}</title>` +
        `<style>@media print{body{margin:0;}}body{margin:16px;}img{max-width:100%;}</style>` +
        `</head><body>${headerHtml}${bodyHtml}</body></html>`;
}

export function printCurrentMessage(): void {
    if (!currentMessage) return;
    const doc = buildPrintableDoc(currentMessage);
    if (!doc) return;

    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;left:-9999px;width:1px;height:1px;border:0;";
    iframe.srcdoc = doc;
    iframe.addEventListener("load", () => {
        const win = iframe.contentWindow;
        if (!win) { iframe.remove(); return; }
        // afterprint fires when the dialog closes (Chromium/WebView2). A
        // fallback timeout covers hosts that don't emit it so the hidden
        // iframe never leaks.
        let removed = false;
        const cleanup = (): void => { if (!removed) { removed = true; iframe.remove(); } };
        win.addEventListener("afterprint", cleanup, { once: true });
        setTimeout(cleanup, 120_000);
        try { win.focus(); win.print(); }
        catch (e) { console.error("[print] failed:", e); cleanup(); }
    }, { once: true });
    document.body.appendChild(iframe);
}

/** Save a Blob to disk. Native Save-As dialog where the File System Access
 *  API exists (WebView2/Chromium — which also stamps the file with the
 *  Mark-of-the-Web, so Office/SmartScreen treat it as untrusted); falls back
 *  to a programmatic <a download> elsewhere. */
async function saveBlobAs(suggestedName: string, blob: Blob): Promise<void> {
    const picker = (window as any).showSaveFilePicker;
    if (typeof picker === "function") {
        let handle: any;
        try {
            handle = await picker({ suggestedName });
        } catch (e: any) {
            if (e?.name === "AbortError") return; // user cancelled
            throw e;
        }
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        return;
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = suggestedName; a.style.display = "none";
    document.body.appendChild(a); a.click();
    setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 5000);
}

/** Save the open message's raw RFC 822 source as a .eml file — the portable
 *  mail format Outlook / Thunderbird / other clients open directly (an .eml
 *  with multipart/related content is what .mht wraps; .eml additionally keeps
 *  the real headers and attachments). */
export async function saveCurrentMessageAsEml(): Promise<void> {
    if (!currentMessage) return;
    try {
        const src = await getMessageSource(currentAccountId, currentMessage.uid, currentMessage.folderId);
        const bytes = Uint8Array.from(atob(src.dataBase64), c => c.charCodeAt(0));
        await saveBlobAs(src.filename || "message.eml", new Blob([bytes], { type: "message/rfc822" }));
    } catch (e: any) {
        window.dispatchEvent(new CustomEvent("mailx-alert", { detail: { message: `Couldn't save message: ${e?.message || e}`, key: "message-save" } }));
    }
}

/** Save the open message as a standalone HTML file — the same header block +
 *  body document the Print path renders, so the saved file reads like the
 *  printed letter. */
export async function saveCurrentMessageAsHtml(): Promise<void> {
    if (!currentMessage) return;
    const doc = buildPrintableDoc(currentMessage);
    if (!doc) return;
    const base = (currentMessage.subject || "message").replace(/[<>:"/\\|?*\r\n]/g, "_").slice(0, 80).trim() || "message";
    try {
        await saveBlobAs(`${base}.html`, new Blob([doc], { type: "text/html" }));
    } catch (e: any) {
        window.dispatchEvent(new CustomEvent("mailx-alert", { detail: { message: `Couldn't save message: ${e?.message || e}`, key: "message-save" } }));
    }
}

/** Build a floating overlay carrying a snapshot of the message: header
 *  (subject, from, to, date) + sandboxed body iframe + attachment chips.
 *  Reuses the compose-overlay drag/resize/close pattern. Independent of the
 *  main viewer — opening pop-out for message A then switching the main pane
 *  to message B leaves A visible in its overlay. */
function spawnDesktopPopout(msg: any, accountId: string): void {
    const wrapper = document.createElement("div");
    wrapper.className = "compose-overlay viewer-popout";
    wrapper.style.cssText = "position:fixed;top:60px;right:20px;width:min(720px,55vw);height:min(800px,80vh);z-index:1000;border:1px solid var(--color-border, #ccc);border-radius:8px;box-shadow:0 8px 32px rgba(0,0,0,0.3);display:flex;flex-direction:column;background:var(--color-bg, #fff);resize:both;overflow:hidden;";

    const titleBar = document.createElement("div");
    titleBar.style.cssText = "display:flex;align-items:center;justify-content:space-between;padding:6px 10px;background:var(--color-bg-alt, #e8ecf0);color:var(--color-text, #000);border-radius:8px 8px 0 0;cursor:move;user-select:none;flex-shrink:0;font-size:13px;";
    const titleText = document.createElement("span");
    titleText.style.cssText = "overflow:hidden;text-overflow:ellipsis;white-space:nowrap;flex:1;font-weight:600;";
    titleText.textContent = msg.subject || "(no subject)";
    titleBar.appendChild(titleText);

    const closeBtn = document.createElement("button");
    closeBtn.textContent = "✕";
    closeBtn.title = "Close pop-out";
    closeBtn.style.cssText = "background:none;border:none;font-size:16px;cursor:pointer;color:#666;padding:2px 8px;border-radius:4px;flex-shrink:0;";
    closeBtn.addEventListener("mouseenter", () => closeBtn.style.color = "#c00");
    closeBtn.addEventListener("mouseleave", () => closeBtn.style.color = "#666");
    closeBtn.addEventListener("click", () => wrapper.remove());
    titleBar.appendChild(closeBtn);

    const headerInfo = document.createElement("div");
    headerInfo.style.cssText = "padding:8px 12px;border-bottom:1px solid var(--color-border, #ddd);font-size:13px;line-height:1.4;flex-shrink:0;";
    const formatAddrLocal = (a: { name?: string; address: string }) =>
        a.name ? `${a.name} <${a.address}>` : a.address;
    const fromStr = formatAddrLocal(msg.from || { address: "" });
    const toStr = (msg.to || []).map(formatAddrLocal).join(", ");
    const ccStr = msg.cc?.length ? ` Cc: ${msg.cc.map(formatAddrLocal).join(", ")}` : "";
    const dateStr = msg.date ? new Date(msg.date).toLocaleString() : "";
    headerInfo.innerHTML =
        `<div><strong>${escapeHtmlLocal(fromStr)}</strong></div>` +
        `<div style="color:var(--color-text-muted, #666)">To: ${escapeHtmlLocal(toStr)}${escapeHtmlLocal(ccStr)}</div>` +
        `<div style="color:var(--color-text-muted, #666);font-size:12px">${escapeHtmlLocal(dateStr)}</div>`;

    const bodyContainer = document.createElement("div");
    bodyContainer.style.cssText = "flex:1;overflow:hidden;display:flex;";
    if (msg.bodyHtml) {
        const iframe = document.createElement("iframe");
        iframe.sandbox.add("allow-same-origin");
        iframe.sandbox.add("allow-popups");
        iframe.sandbox.add("allow-popups-to-escape-sandbox");
        iframe.sandbox.add("allow-top-navigation-by-user-activation");
        iframe.sandbox.add("allow-scripts");
        iframe.srcdoc = wrapHtmlBody(msg.bodyHtml, msg.remoteAllowed);
        iframe.style.cssText = "flex:1;border:none;width:100%;background:#fff;";
        bodyContainer.appendChild(iframe);
        // Same zoom + key-forwarding controls as the main viewer iframe —
        // Ctrl+wheel / Ctrl± / Ctrl+0 and the persisted `mailx-preview-zoom`.
        // Without this the pop-out had no zoom at all (Bob 2026-05-21).
        installPreviewControls(iframe);
    } else {
        const pre = document.createElement("pre");
        pre.style.cssText = "padding:12px;white-space:pre-wrap;word-break:break-word;margin:0;flex:1;overflow:auto;";
        pre.textContent = msg.bodyText || "(no content)";
        bodyContainer.appendChild(pre);
    }

    // Drag — same pattern as compose-overlay: pointer-events:none on the
    // iframe so cursor crossing into it doesn't lose drag events.
    let dragX = 0, dragY = 0;
    titleBar.addEventListener("mousedown", (e: MouseEvent) => {
        if (e.target === closeBtn) return;
        e.preventDefault();
        const rect = wrapper.getBoundingClientRect();
        dragX = e.clientX - rect.left;
        dragY = e.clientY - rect.top;
        const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
        bodyContainer.style.pointerEvents = "none";
        document.body.style.userSelect = "none";
        const onMove = (ev: MouseEvent) => {
            ev.preventDefault();
            const left = clamp(ev.clientX - dragX, 0, window.innerWidth - 40);
            const top = clamp(ev.clientY - dragY, 0, window.innerHeight - 40);
            wrapper.style.left = `${left}px`;
            wrapper.style.top = `${top}px`;
            wrapper.style.right = "auto";
            wrapper.style.bottom = "auto";
        };
        const onUp = () => {
            bodyContainer.style.pointerEvents = "";
            document.body.style.userSelect = "";
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onUp);
        };
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
    });

    // Bring to front on click — shared with compose-overlay so they all
    // restack uniformly.
    wrapper.addEventListener("mousedown", () => {
        document.querySelectorAll(".compose-overlay").forEach(el => (el as HTMLElement).style.zIndex = "1000");
        wrapper.style.zIndex = "1001";
    });

    // Cascade pop-outs so they don't all stack at the same coords.
    const existing = document.querySelectorAll(".viewer-popout").length;
    if (existing > 0) {
        wrapper.style.top = `${60 + existing * 28}px`;
        wrapper.style.right = `${20 + existing * 28}px`;
    }

    // Action toolbar — icon buttons that MATCH the preview's `.mv-toolbar`
    // (same glyphs/titles) so the popped-out message reads identically to the
    // inline preview, instead of a divergent text toolbar (Bob 2026-06-29: "why
    // isn't that toolbar shared?"). Each button fires `mailx-popout-action`
    // (the popout can't reach app.ts's openCompose directly); app.ts routes it.
    const toolbar = document.createElement("div");
    toolbar.className = "mv-toolbar";
    toolbar.style.cssText = "display:flex;gap:4px;padding:6px 12px;border-bottom:1px solid var(--color-border, #ddd);flex-shrink:0;";
    const mkPopoutBtn = (glyph: string, title: string, onClick: () => void): HTMLButtonElement => {
        const b = document.createElement("button");
        b.className = "tb-btn";
        b.textContent = glyph;
        b.title = title;
        b.style.cssText = "padding:4px 9px;font-size:15px;cursor:pointer;border:1px solid var(--color-border, #ccc);border-radius:4px;background:var(--color-bg, #fff);color:var(--color-text, #000);";
        b.addEventListener("click", onClick);
        return b;
    };
    const firePopoutAction = (action: string): void => {
        document.dispatchEvent(new CustomEvent("mailx-popout-action", { detail: { action, msg, accountId } }));
    };
    toolbar.appendChild(mkPopoutBtn("↩", "Reply (Ctrl+R)", () => firePopoutAction("reply")));
    toolbar.appendChild(mkPopoutBtn("↩↩", "Reply All (Ctrl+Shift+R)", () => firePopoutAction("replyAll")));
    toolbar.appendChild(mkPopoutBtn("→", "Forward", () => firePopoutAction("forward")));
    toolbar.appendChild(mkPopoutBtn("⧉", "Edit as new message", () => firePopoutAction("editAsNew")));
    toolbar.appendChild(mkPopoutBtn("★", "Flag", () => firePopoutAction("flag")));
    toolbar.appendChild(mkPopoutBtn("🗑", "Delete", () => {
        firePopoutAction("delete");
        wrapper.remove();   // the message is gone — close its window too
    }));

    wrapper.appendChild(titleBar);
    wrapper.appendChild(headerInfo);
    wrapper.appendChild(toolbar);
    wrapper.appendChild(bodyContainer);
    document.body.appendChild(wrapper);
}

function escapeHtmlLocal(s: string): string {
    return (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Background body-fetch failure — when the user clicked an uncached row
// and the reconciler's IMAP fetch failed (rate limit, network drop, auth),
// surface a banner above the (empty) body for the *currently-shown* message
// so the user knows the spinner isn't going to resolve. Other windows /
// stale rows are ignored — no banner clutter for messages they walked away
// from. The reconciler retries on its own cadence; no client-side retry.
subscribeStore("*", (ev: any) => {
    if (ev.kind !== "bodyFetchError") return;
    // Always record — even if the failed message isn't the current
    // viewer target — so a later click on that row can surface the
    // reason instead of "No content".
    recentFetchErrors.set(`${ev.accountId}:${ev.uid}`, {
        error: String(ev.error || "fetch failed"),
        transient: !!ev.transient,
        when: Date.now(),
    });
    if (!currentMessage || currentAccountId !== ev.accountId) return;
    if (currentMessage.uid !== ev.uid) return;
    const bodyEl = document.getElementById("mv-body");
    if (!bodyEl) return;
    if (bodyEl.querySelector(".mv-body-fetch-error")) return; // already shown
    const banner = document.createElement("div");
    banner.className = "mv-system-message mv-system-error mv-body-fetch-error";
    const transientNote = ev.transient
        ? "Will retry automatically."
        : "Move/delete may have raced with the server.";
    banner.innerHTML = `
        <div class="mv-system-tag">mailx</div>
        <div class="mv-system-title">Couldn't fetch message body</div>
        <div class="mv-system-body">${escapeHtmlLocal(String(ev.error || "fetch failed"))}<br><span style="color:var(--color-text-muted);font-size:0.9em">${transientNote}</span></div>`;
    const placeholder = bodyEl.querySelector(".mv-empty");
    if (placeholder) placeholder.replaceWith(banner);
    else bodyEl.prepend(banner);
});

// `draftSaved` arrives when the compose window's autosave or Ctrl+S lands
// new body content on disk for the message currently displayed in the
// preview pane. Without re-rendering, the preview shows the BEFORE-save
// body until the user clicks the row again. Match by `previousDraftUid` —
// that's the UID currently displayed; the daemon will eventually IMAP
// APPEND a fresh UID and delete the old one, but until that round-trip
// lands `currentMessage.uid` still refers to the old UID.
onEvent((ev: any) => {
    if (!ev || ev.type !== "draftSaved") return;
    if (!currentMessage || currentAccountId !== ev.accountId) return;
    if (currentMessage.uid !== ev.previousDraftUid) return;
    const newBody = String(ev.bodyHtml || "");
    currentMessage.bodyHtml = newBody;
    if (ev.subject) currentMessage.subject = ev.subject;
    const bodyEl = document.getElementById("mv-body");
    const iframe = bodyEl?.querySelector("iframe") as HTMLIFrameElement | null;
    if (iframe) {
        iframe.srcdoc = wrapHtmlBody(newBody, !!(currentMessage as any).remoteAllowed);
    }
    const subjEl = document.querySelector(".mv-subject");
    if (subjEl && ev.subject) subjEl.textContent = String(ev.subject);
});
